import React, { useState, useEffect, useMemo } from "react";
import { Refresh, BarChart, ListAlt, LocationOn } from "@mui/icons-material";
import { useSelector } from "react-redux";
import DaterangePicker from "../../common/Daterangepicker";
import Citiescommondropdown from "../../common/Citiescommondropdown";
import MetricAIAssistant from '../../common/MetricAIAssistant';

export default function VehicleRevenueDashboard() {
  const cities = useSelector((state) => state?.cities?.citiesData);
  const [cityFilter, setCityFilter] = useState(null);

  const cityOptions = useMemo(() => {
    if (!cities) return [];
    return Object.entries(cities).map(([key, value]) => ({
      value: key,
      label: value,
    }));
  }, [cities]);

  const [viewMode, setViewMode] = useState("numerical");
  const [page, setPage] = useState(0);
  const [showLossVehicles, setShowLossVehicles] = useState(false);
  const [activeTab, setActiveTab] = useState("performance");
  const [searchQuery, setSearchQuery] = useState("");
  const [tablePage, setTablePage] = useState(0);
  const tableRowsPerPage = 8;

  const profile = JSON.parse(localStorage.getItem("profile"));
  const token = localStorage.getItem("token");
  const vehicleStatus =
    JSON.parse(localStorage.getItem("catalogValues"))?.vehicleStatus || {};
  const vehicleBodyType =
    JSON.parse(localStorage.getItem("catalogValues"))?.vehicleBodyType || {};
  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  const rowsPerPage = 5;

  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(today.getMonth() - 1);

  const [selectionRange, setSelectionRange] = useState({
    startDate: oneMonthAgo,
    endDate: today,
    key: "selection",
  });

  const [city, setCity] = useState("");

  const cityColors = {
    Bangalore: "#D43111",
    Mysuru: "#1F23DC",
    Chennai: "#BAFF1A",
    Vellore: "#1B7C81",
  };
  const [hoveredCity, setHoveredCity] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  function formatDateForApi(date, type) {
    if (!date) return "";
    const pad = (n) => (n < 10 ? "0" + n : n);
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    let hours = type === "start" ? "00" : "23";
    let minutes = type === "start" ? "00" : "59";
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  useEffect(() => {
    const fetchDashboardInfo = async () => {
      try {
        setLoading(true);
        const formattedStartDate = formatDateForApi(
          selectionRange?.startDate,
          "start",
        );
        const formattedEndDate = formatDateForApi(
          selectionRange?.endDate,
          "end",
        );

        let query = `startDate=${formattedStartDate}&endDate=${formattedEndDate}`;
        if (cityFilter) query += `&cityId=${cityFilter}`;

        const [res1, res2, res3] = await Promise.all([
          fetch(
            `${apiUrl}/api/dashboardreports/vehicle-wise-revenue-count-performance?${query}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: `${token}`,
              },
            },
          ),
          fetch(
            `${apiUrl}/api/dashboardreports/vehicle-wise-revenue-performance-tier?${query}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: `${token}`,
              },
            },
          ),
          fetch(
            `${apiUrl}/api/dashboardreports/vehicle-wise-revenue-details?${query}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: `${token}`,
              },
            },
          ),
        ]);
        const api1 = await res1.json();
        const api2 = await res2.json();
        const api3 = await res3.json();
        // Parse API 1
        const efficiencyRate = api1.dataValues?.efficiencyRate || "0.0%";
        const fleetSize = api1.dataValues?.totalVehicles || 0;
        const avgActiveDays = api1.dataValues?.avgActiveDays || "0";
        const targetUtilization = api1.dataValues?.targetUtilization || "0%";
        const topPerformersData = api1.dataValues?.topPerformer || [];
        const topLosserData = api1.dataValues?.topLosser || [];

        // Parse API 2
        const totalBookings = api2.dataValues?.totalBookings || 0;
        const rawVehicleList = api2.dataValues?.dataList || [];

        // Parse API 3
        const additionalDataList = api3.dataValues?.dataList || [];
        const chargesTotal = api3.dataValues?.chargesTotal || {};

        // Create map of additional data from API 3
        const additionalDataMap = {};
        additionalDataList.forEach((item) => {
          additionalDataMap[item.id] = {
            fuel: item.fuel || 0,
            extraHrs: item.extraHrs || 0,
            extraKm: item.extraKm || 0,
            downtimeCharges: item.downtimeCharges || 0,
            extraHrPenalty: item.extraHrPenalty || 0,
            status: item.status || null,
            bodyTypeText: item.bodyTypeText || "",
            deliveryAndPickupCharges: item.deliveryAndPickupCharges || 0,
          };
        });

        // Calculate total revenue
        const totalRevenue = rawVehicleList.reduce(
          (acc, v) => acc + (parseFloat(v.totalRevenue) || 0),
          0,
        );

        // Merge all data - USE DIRECT FIELDS FROM API
        const mergedVehicleList = rawVehicleList
          .map((v) => {
            const additionalData = additionalDataMap[v.id] || {};
            return {
              ...v,
              totalRevenue: parseFloat(v.totalRevenue) || 0,
              bookingCount: parseInt(v.bookingCount, 10) || 0,
              averageRevenuePerDay: parseFloat(v.averageRevenuePerDay) || 0,
              imageUrl: v.imageUrl || "",
              // USE THESE FIELDS DIRECTLY FROM API
              fuel: v.fuel || 0,
              extraHrs: v.extraHrs || 0,
              extraKm: v.extraKm || 0,
              downtimeCharges: v.downtimeCharges || 0,
              deliveryAndPickupCharges: v.deliveryAndPickupCharges || 0,
              extraHrPenalty: v.extraHrPenalty || 0,
              status: v.status || "179",
              bodyTypeText: v.bodyTypeText || "",
              ...additionalData,
            };
          })
          .sort((a, b) => b.totalRevenue - a.totalRevenue);

        // Get status from API 1
        const allVehiclesWithStatus = [...topPerformersData, ...topLosserData];
        const statusMap = {};
        allVehiclesWithStatus.forEach((v) => {
          statusMap[v.id] = v.status || "179";
        });

        // Active vehicles
        const activeVehicles = mergedVehicleList.filter((v) => {
          const status = statusMap[v.id] || "179";
          return String(status) === "179";
        }).length;

        // Calculate average occupancy
        const avgOccupancy =
          mergedVehicleList.length > 0
            ? Number(
                (
                  mergedVehicleList.reduce(
                    (acc, v) =>
                      acc +
                      ((v.bookingCount * v.avgDuration) /
                        (v.totalDaysOperated || 1)) *
                        100,
                    0,
                  ) / mergedVehicleList.length
                ).toFixed(2),
              )
            : 0;

        // Loss vehicles
        const lossVehicles = mergedVehicleList.filter((v) => {
          const status = statusMap[v.id] || "179";
          return String(status) === "179" && Number(v.totalRevenue) <= 0;
        });

        // Top performers
        const topPerformers = topPerformersData.slice(0, 3).map((tp) => ({
          id: tp.id,
          name: tp.name,
          licensePlate: tp.licensePlate,
          cityText: tp.cityText,
          imageUrl: tp.imageUrl,
          totalRevenue: parseFloat(tp.totalRevenue) || 0,
          averageRevenuePerDay: parseFloat(tp.averageRevenuePerDay) || 0,
          bookingCount: parseInt(tp.bookingCount, 10) || 0,
          totalDaysOperated: tp.totalDaysOperated || 0,
          achievement: tp.achivement || 0,
          isTargetMet: tp.isTargetMet || false,
          daysRemaining: tp.daysRemaining || 0,
        }));

        // Performance tiers
        const sortedByRevenue = [...mergedVehicleList]
          .filter((v) => v.totalRevenue > 0)
          .sort((a, b) => b.totalRevenue - a.totalRevenue);
        const totalRevenueVehicles = sortedByRevenue.length;

        let highestRevenueVehicles = [];
        let moderateRevenueVehicles = [];
        let lowestRevenueVehicles = [];

        if (totalRevenueVehicles >= 3) {
          highestRevenueVehicles = sortedByRevenue.slice(
            0,
            Math.min(5, Math.ceil(totalRevenueVehicles * 0.4)),
          );
          const remaining = sortedByRevenue.slice(
            highestRevenueVehicles.length,
          );
          lowestRevenueVehicles = remaining.slice(
            -Math.min(5, Math.ceil(totalRevenueVehicles * 0.4)),
          );
          const middleStart = highestRevenueVehicles.length;
          const middleEnd = remaining.length - lowestRevenueVehicles.length;
          moderateRevenueVehicles = remaining
            .slice(middleStart, middleEnd)
            .slice(0, 5);
        } else {
          highestRevenueVehicles = sortedByRevenue.slice(
            0,
            Math.min(5, totalRevenueVehicles),
          );
          lowestRevenueVehicles = [];
          moderateRevenueVehicles = [];
        }

        setDashboardData({
          efficiencyRate,
          fleetSize,
          avgActiveDays,
          targetUtilization,
          topPerformers,
          topLosserData,
          totalRevenue,
          totalBookings: rawVehicleList.reduce(
            (acc, v) => acc + (parseInt(v.bookingCount) || 0),
            0,
          ),
          avgOccupancy,
          activeVehicles,
          revenueList: mergedVehicleList,
          lossVehicles,
          highestRevenueVehicles,
          moderateRevenueVehicles,
          lowestRevenueVehicles,
          chargesTotal,
          additionalDataMap,
          statusMap,
        });
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchDashboardInfo();
    } else {
      setLoading(false);
    }
  }, [selectionRange, cityFilter, token, apiUrl]);

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return (
      "₹" +
      parseFloat(amount).toLocaleString("en-IN", { maximumFractionDigits: 2 })
    );
  };

  // Copy to clipboard
  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Handle sorting
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Get sorted data
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      let aVal = a[sortConfig.key] ?? "";
      let bVal = b[sortConfig.key] ?? "";
      if (typeof aVal === "string") aVal = aVal.toLowerCase();
      if (typeof bVal === "string") bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  // Export CSV
  const exportCSV = () => {
    const headers = [
      "Vehicle",
      "Plate",
      "Type",
      "Status",
      "Total Revenue",
      "ADR/Day",
      "Avg Duration",
      "Target Achieved",
    ];
    const rows = filteredData.map((row) => [
      row.name || "-",
      row.licensePlate || "UNREGISTERED",
      row.bodyTypeText || "SUV",
      row.status === 179 ? "Active" : "Inactive",
      row.totalRevenue || 0,
      row.averageRevenuePerDay || 0,
      row.avgDuration || 0,
      `${Math.round(row.achivement || 0)}%`,
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((r) => r.join(",")),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vehicle_revenue_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
  };

  // Export PDF
  const exportPDF = () => {
    // Using window.print() for simple PDF generation
    window.print();
    setShowExportMenu(false);
  };

  const filteredData = useMemo(() => {
    if (!dashboardData?.revenueList) return [];
    const filtered = dashboardData.revenueList.filter(
      (item) =>
        item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.licensePlate?.toLowerCase().includes(searchQuery.toLowerCase()),
    );
    // Apply sorting
    if (sortConfig.key) {
      return [...filtered].sort((a, b) => {
        let aVal = a[sortConfig.key] ?? "";
        let bVal = b[sortConfig.key] ?? "";
        if (typeof aVal === "string") aVal = aVal.toLowerCase();
        if (typeof bVal === "string") bVal = bVal.toLowerCase();
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }, [dashboardData?.revenueList, searchQuery, sortConfig]);

  const paginatedTableData = filteredData.slice(
    tablePage * tableRowsPerPage,
    (tablePage + 1) * tableRowsPerPage,
  );

  if (loading || !dashboardData) {
    return (
      <div className="w-full min-h-screen bg-slate-50 flex items-center justify-center font-['Inter']">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-500 font-medium">Launching dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-slate-50 font-['Inter'] pb-12 my-6">
      <div className="w-full bg-white border-b border-[#E5E7EB] shadow-sm px-8 py-5 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-gray-900 text-2xl font-bold tracking-tight">
            Vehicle Revenue Dashboard
          </h1>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all duration-200 border border-gray-200 shadow-sm"
            title="How metrics are calculated"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </button>
        </div>

        <DaterangePicker
          selectionRange={selectionRange}
          setSelectionRange={setSelectionRange}
          style={() => {}}
          renderInput={({ open, setOpen, selectionRange, onClick }) => (
            <div
              onClick={onClick}
              className="flex items-center gap-2 min-w-[240px] shadow-sm rounded-xl border border-gray-200 bg-white px-3 py-2 cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                className="flex-shrink-0"
              >
                <path
                  d="M5.3335 1.33334V4.00001"
                  stroke="#99A1AF"
                  strokeWidth="1.33333"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10.6665 1.33334V4.00001"
                  stroke="#99A1AF"
                  strokeWidth="1.33333"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12.6667 2.66666H3.33333C2.59695 2.66666 2 3.26361 2 3.99999V13.3333C2 14.0697 2.59695 14.6667 3.33333 14.6667H12.6667C13.403 14.6667 14 14.0697 14 13.3333V3.99999C14 3.26361 13.403 2.66666 12.6667 2.66666Z"
                  stroke="#99A1AF"
                  strokeWidth="1.33333"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M2 6.66666H14"
                  stroke="#99A1AF"
                  strokeWidth="1.33333"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="flex-1 text-[#101828] font-['Inter'] text-sm font-semibold leading-5">
                {selectionRange.startDate && selectionRange.endDate
                  ? `${selectionRange.startDate.toLocaleDateString()} - ${selectionRange.endDate.toLocaleDateString()}`
                  : "Select dates"}
              </span>
            </div>
          )}
        />

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          <div className="min-w-[180px] px-3 py-2 bg-white rounded-[10px] shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10)] shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10)] outline outline-[0.80px] outline-offset-[-0.80px] outline-gray-200 inline-flex justify-start items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              className="flex-shrink-0 w-4 h-4"
            >
              <path
                d="M8.4005 14.5327C9.6405 13.462 13.3332 9.99534 13.3332 6.66668C13.3332 5.25219 12.7713 3.89563 11.7711 2.89544C10.7709 1.89525 9.41433 1.33334 7.99984 1.33334C6.58535 1.33334 5.2288 1.89525 4.2286 2.89544C3.22841 3.89563 2.6665 5.25219 2.6665 6.66668C2.6665 9.99534 6.35917 13.462 7.59917 14.5327C7.71469 14.6195 7.85531 14.6665 7.99984 14.6665C8.14437 14.6665 8.28499 14.6195 8.4005 14.5327Z"
                stroke="#99A1AF"
                strokeWidth="1.33333"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8 8.66666C9.10457 8.66666 10 7.77123 10 6.66666C10 5.56209 9.10457 4.66666 8 4.66666C6.89543 4.66666 6 5.56209 6 6.66666C6 7.77123 6.89543 8.66666 8 8.66666Z"
                stroke="#99A1AF"
                strokeWidth="1.33333"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            <Citiescommondropdown
              value={cityFilter}
              onChange={setCityFilter}
              options={[{ value: null, label: "All Cities" }, ...cityOptions]}
              placeholder="City"
              className="flex-1 min-w-[80px]"
              showSearch={true}
            />
          </div>

          <button
            onClick={() => window.location.reload()}
            className="p-2 text-rose-600 border border-rose-200 hover:bg-rose-50 rounded-xl transition duration-200 flex items-center justify-center shadow-sm"
          >
            <Refresh fontSize="small" />
          </button>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto p-8 flex flex-col gap-8">
        {showInfo && (
          <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm p-6 animate-fade-in">
            {/* Close button - aligned right */}
            <div className="flex justify-end items-start mb-4">
              <button
                onClick={() => setShowInfo(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <h3 className="text-gray-900 text-lg font-bold leading-7 mb-5 flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#4F46E5"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              How Metrics Are Calculated
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Metric 1: Total Revenue */}
              <div className="bg-white p-4 rounded-xl border border-[#E5E7EB] shadow-sm hover:border-gray-300 transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center">
                    <span className="text-indigo-600 font-bold text-sm">₹</span>
                  </div>
                  <h4 className="text-gray-900 font-semibold text-sm">
                    Total Revenue
                  </h4>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Sum of all revenue generated by all vehicles in the selected
                  period.
                </p>
                <div className="mt-2 p-2 bg-indigo-50/50 rounded-lg border border-indigo-100">
                  <code className="text-xs text-indigo-800 font-medium">
                    Σ (Revenue from all vehicles)
                  </code>
                </div>
              </div>

              {/* Metric 2: Efficiency Rate */}
              <div className="bg-white p-4 rounded-xl border border-[#E5E7EB] shadow-sm hover:border-gray-300 transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#059669"
                      strokeWidth="2"
                    >
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                  </div>
                  <h4 className="text-gray-900 font-semibold text-sm">
                    Efficiency Rate
                  </h4>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Percentage of vehicles meeting their revenue targets.
                </p>
                <div className="mt-2 p-2 bg-emerald-50/50 rounded-lg border border-emerald-100">
                  <code className="text-xs text-emerald-800 font-medium">
                    (Vehicles meeting target / Total vehicles) × 100
                  </code>
                </div>
              </div>

              {/* Metric 3: Average Occupancy */}
              <div className="bg-white p-4 rounded-xl border border-[#E5E7EB] shadow-sm hover:border-gray-300 transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#2563EB"
                      strokeWidth="2"
                    >
                      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                    </svg>
                  </div>
                  <h4 className="text-gray-900 font-semibold text-sm">
                    Average Occupancy
                  </h4>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Average percentage of days vehicles were operational.
                </p>
                <div className="mt-2 p-2 bg-blue-50/50 rounded-lg border border-blue-100">
                  <code className="text-xs text-blue-800 font-medium">
                    Average of (Active days / Total days in period) × 100
                  </code>
                </div>
              </div>

              {/* Metric 4: Average Active Days */}
              <div className="bg-white p-4 rounded-xl border border-[#E5E7EB] shadow-sm hover:border-gray-300 transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#7C3AED"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                  </div>
                  <h4 className="text-gray-900 font-semibold text-sm">
                    Average Active Days
                  </h4>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Average number of days vehicles were operational during the
                  period.
                </p>
                <div className="mt-2 p-2 bg-purple-50/50 rounded-lg border border-purple-100">
                  <code className="text-xs text-purple-800 font-medium">
                    Average of (Total days each vehicle operated)
                  </code>
                </div>
              </div>

              {/* Metric 5: Booking Gap */}
              <div className="bg-white p-4 rounded-xl border border-[#E5E7EB] shadow-sm hover:border-gray-300 transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#EA580C"
                      strokeWidth="2"
                    >
                      <path d="M12 2v4M12 18v4M4 4l2 2M18 18l2 2M4 20l2-2M18 6l2-2" />
                      <circle cx="12" cy="12" r="7" />
                    </svg>
                  </div>
                  <h4 className="text-gray-900 font-semibold text-sm">
                    Average Booking Gap
                  </h4>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Average days between bookings for each vehicle.
                </p>
                <div className="mt-2 p-2 bg-orange-50/50 rounded-lg border border-orange-100">
                  <code className="text-xs text-orange-800 font-medium">
                    Active Days / Number of Bookings
                  </code>
                </div>
              </div>

              {/* Metric 6: ADR */}
              <div className="bg-white p-4 rounded-xl border border-[#E5E7EB] shadow-sm hover:border-gray-300 transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-rose-50 rounded-lg flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#E11D48"
                      strokeWidth="2"
                    >
                      <path d="M12 2v20M17 7H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                    </svg>
                  </div>
                  <h4 className="text-gray-900 font-semibold text-sm">
                    ADR (Avg Daily Revenue)
                  </h4>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Average revenue generated per operational day.
                </p>
                <div className="mt-2 p-2 bg-rose-50/50 rounded-lg border border-rose-100">
                  <code className="text-xs text-rose-800 font-medium">
                    Total Revenue / Total Active Days
                  </code>
                </div>
              </div>
            </div>

            {/* Status Indicators - matching your dashboard's status styles */}
            <div className="mt-5 p-4 bg-gray-50 rounded-xl border border-[#E5E7EB]">
              <h4 className="text-gray-900 font-semibold text-sm mb-3">
                Status Indicators
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-[#006240] rounded-full"></div>
                  <span className="text-gray-600 text-sm font-medium">
                    On track / High performer
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-[#0D2E9B] rounded-full"></div>
                  <span className="text-gray-600 text-sm font-medium">
                    Moderate / Needs improvement
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-[#FF8C00] rounded-full"></div>
                  <span className="text-gray-600 text-sm font-medium">
                    Below average / Needs push
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-[#AB0000] rounded-full"></div>
                  <span className="text-gray-600 text-sm font-medium">
                    Critical / Zero revenue
                  </span>
                </div>
              </div>
            </div>

            {/* Note - matching your dashboard's subtle text style */}
            <div className="mt-3 text-gray-400 text-sm font-medium">
              All metrics are calculated based on the selected date range.
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="h-[232px] w-full relative rounded-[14px] bg-gradient-to-br from-[#2111be] to-[#5149db] border-b-4 border-[#b1bbff] overflow-hidden flex-shrink-0 text-left text-sm text-[#FFFFFF] font-inter shadow-md flex flex-col justify-between p-6">
            <div className="absolute right-[-10px] top-[-1px] opacity-90 text-white pointer-events-none">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="139"
                height="137"
                viewBox="0 0 139 137"
                fill="none"
              >
                <path
                  d="M80 -9.86588V123.467"
                  stroke="#615FFF"
                  strokeOpacity="0.2"
                  strokeWidth="13.3333"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M113.333 10.1341H63.3333C57.1449 10.1341 51.21 12.5925 46.8342 16.9683C42.4583 21.3441 40 27.2791 40 33.4675C40 39.6558 42.4583 45.5908 46.8342 49.9666C51.21 54.3425 57.1449 56.8008 63.3333 56.8008H96.6667C102.855 56.8008 108.79 59.2591 113.166 63.635C117.542 68.0108 120 73.9457 120 80.1341C120 86.3225 117.542 92.2574 113.166 96.6333C108.79 101.009 102.855 103.467 96.6667 103.467H40"
                  stroke="#615FFF"
                  strokeOpacity="0.2"
                  strokeWidth="13.3333"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div>
              <div className="flex justify-between items-center w-full">
                <span className="text-[#FFFFFF] text-xs font-bold uppercase tracking-wider">
                  Total Revenue
                </span>
                <div className="bg-white rounded-full flex items-center justify-center">
                <MetricAIAssistant 
                  metricName="Total Revenue"
                  metricData={{
                    totalRevenue: dashboardData.totalRevenue,
                    targetUtilization: dashboardData.targetUtilization,
                    city: cityFilter,
                    dateRange: selectionRange
                  }}
                  token={token}
                />
                </div>
              </div>
              <h2 className="text-[48px] font-black tracking-tight mt-2 pt-4">
                {formatCurrency(dashboardData.totalRevenue)}
              </h2>
            </div>
            <div className="flex items-center gap-3 mt-4">
              <span className="px-2.5 py-1 bg-emerald-500/20 text-emerald-300 rounded-lg text-xs font-semibold flex items-center gap-1">
                Achieved {dashboardData.targetUtilization}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M4.6665 4.66669H11.3332V11.3334"
                    stroke="#5EE9B5"
                    strokeWidth="1.33333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4.6665 11.3334L11.3332 4.66669"
                    stroke="#5EE9B5"
                    strokeWidth="1.33333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <div className="bg-white p-5 rounded-2xl border border-[#E5E7EB] shadow-sm flex flex-col justify-between w-full h-[108px]">
              <div>
                <div className="flex justify-between items-center w-full">
                <span className="text-slate-400 text-sm font-semibold tracking-wide">
                  Efficiency Rate
                </span>
                <MetricAIAssistant 
                    metricName="Efficiency Rate"
                    metricData={{
                      efficiencyRate: dashboardData.efficiencyRate,
                      avgRevenuePerVehicle: dashboardData.revenueList.reduce(
                        (acc, v) => acc + (v.totalRevenue || 0),
                        0,
                      ) / dashboardData.revenueList.length || 0,
                      fleetSize: dashboardData.fleetSize,
                      city: cityFilter
                    }}
                    token={token}
                  />
                  </div>
                <div className="flex items-center justify-between mt-1">
                  <div className="text-gray-900 text-3xl font-bold">
                    {dashboardData.efficiencyRate}
                  </div>
                  <p className="text-sm text-gray-600">
                    Avg Revenue:{" "}
                    {formatCurrency(
                      dashboardData.revenueList.reduce(
                        (acc, v) => acc + (v.totalRevenue || 0),
                        0,
                      ) / dashboardData.revenueList.length || 0,
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-[#E5E7EB] shadow-sm flex flex-col justify-between w-full h-[108px]">
              <div>
                <div className="flex justify-between items-center w-full">
                <span className="text-slate-400 text-sm font-semibold tracking-wide">
                  Total Bookings
                </span>
                <MetricAIAssistant 
                    metricName="Total Bookings"
                    metricData={{
                      totalBookings: dashboardData.totalBookings,
                      fleetSize: dashboardData.fleetSize,
                      city: cityFilter,
                      dateRange: selectionRange
                    }}
                    token={token}
                  />
                  </div>
                <div className="flex items-center justify-between mt-1">
                  <div className="text-gray-900 text-3xl font-bold">
                    {dashboardData.totalBookings}
                  </div>
                  <span className="text-xs text-indigo-600 font-semibold bg-indigo-50 px-2 py-0.5 rounded-md">
                    {dashboardData.fleetSize} Vehicles in Count
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white p-5 rounded-2xl border border-[#E5E7EB] shadow-sm flex flex-col justify-between w-full h-[141px]">
              <div>
                <div className="flex justify-between items-center w-full">
                <span className="text-slate-400 text-sm font-semibold tracking-wide">
                  Avg Occupancy
                </span>
                <MetricAIAssistant 
                    metricName="Average Fleet Occupancy"
                    metricData={{
                      avgOccupancy: dashboardData.avgOccupancy,
                      fleetSize: dashboardData.fleetSize,
                      city: cityFilter
                    }}
                    token={token}
                  />
                  </div>
                <div className="text-gray-900 text-3xl font-bold mt-1">
                  {dashboardData.avgOccupancy}%
                </div>
              </div>
              <p className="text-xs font-medium text-slate-500 mt-2">
                Fleet Average
              </p>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-[#E5E7EB] shadow-sm flex flex-col justify-between w-full h-[141px]">
              <div>
                <div className="flex justify-between items-center w-full">
                <span className="text-slate-400 text-sm font-semibold tracking-wide">
                  Fleet Size
                </span>
                <MetricAIAssistant 
                    metricName="Fleet Size"
                    metricData={{
                      fleetSize: dashboardData.fleetSize,
                      activeVehicles: dashboardData.activeVehicles,
                      city: cityFilter
                    }}
                    token={token}
                  />
                  </div>
                <div className="text-gray-900 text-3xl font-bold mt-1">
                  {dashboardData.fleetSize}
                </div>
              </div>
              <p className="text-xs font-medium text-emerald-600">
                Total Vehicles for Selected City
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white p-5 rounded-2xl border border-[#E5E7EB] shadow-sm flex flex-col justify-between w-full h-[141px]">
              <div>
                <div className="flex justify-between items-center w-full">
                <span className="text-slate-400 text-sm font-semibold tracking-wide">
                  Active Vehicles
                </span>
                <MetricAIAssistant 
                  metricName="Active Vehicles Utilization"
                  metricData={{
                    activeVehicles: dashboardData.activeVehicles,
                    fleetSize: dashboardData.fleetSize,
                    activePercentage: dashboardData.fleetSize ? Math.round((dashboardData.activeVehicles / dashboardData.fleetSize) * 100) : 0,
                    city: cityFilter
                  }}
                  token={token}
                />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-24 p-4 bg-emerald-50 rounded-lg outline outline-1 outline-offset-[-1px] outline-stone-300 flex justify-center items-center">
                  <span className="text-emerald-700 text-xl font-semibold font-['Inter'] leading-4">
                    {dashboardData.activeVehicles}
                  </span>
                  <span className="text-emerald-700 text-xl font-semibold font-['Inter'] leading-4">
                    /
                  </span>
                  <span className="text-emerald-700 text-xl font-semibold font-['Inter'] leading-4">
                    {dashboardData.fleetSize}
                  </span>
                </div>
                <p className="text-md font-medium text-slate-500">Of Fleet</p>
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-[#E5E7EB] shadow-sm flex flex-col justify-between w-full h-[141px]">
              <div>
                <div className="flex justify-between items-center w-full">
                <span className="text-slate-400 text-sm font-semibold tracking-wide">
                  Average Active Days
                </span>
                <MetricAIAssistant 
                    metricName="Average Active Days"
                    metricData={{
                      avgActiveDays: dashboardData.avgActiveDays,
                      activeVehicles: dashboardData.activeVehicles,
                      fleetSize: dashboardData.fleetSize,
                      city: cityFilter
                    }}
                    token={token}
                  />
                  </div>
                <div className="text-gray-900 text-3xl font-bold mt-1">
                  {dashboardData.avgActiveDays}
                </div>
              </div>
              <div className="flex items-center gap-32">
                <p className="text-md font-medium text-slate-500">
                  Active Vehicles
                </p>
                <div className="w-12 h-4 p-2 bg-emerald-50 rounded-lg outline outline-1 outline-offset-[-1px] outline-stone-300 flex justify-center items-center">
                  <span className="text-emerald-700 text-xs font-semibold font-['Inter'] leading-4">
                    {dashboardData.activeVehicles}
                  </span>
                  <span className="text-emerald-700 text-xs font-semibold font-['Inter'] leading-4">
                    /
                  </span>
                  <span className="text-emerald-700 text-xs font-semibold font-['Inter'] leading-4">
                    {dashboardData.fleetSize}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {dashboardData.lossVehicles.length > 0 && (
          <div className="w-full bg-rose-50 border-b-4 border-rose-500 rounded-2xl p-6 flex flex-col shadow-sm gap-4 transition-all">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <span className="text-rose-800 text-sm font-bold uppercase tracking-wider">
                  Zero Revenue / Loss Vehicles
                </span>
                <MetricAIAssistant 
                  metricName="Loss Vehicles"
                  metricData={{
                    lossVehicleCount: dashboardData.lossVehicles.length,
                    totalFleetSize: dashboardData.fleetSize,
                    city: cityFilter,
                    dateRange: selectionRange
                  }}
                  token={token}
                />
                <div className="text-rose-700 text-4xl font-black mt-1">
                  {dashboardData.lossVehicles.length}
                </div>
              </div>
              <div className="flex flex-col items-end gap-3">
                <span className="bg-rose-100 text-rose-800 text-sm px-3 py-1 rounded-full font-semibold">
                  Needs Attention
                </span>
                <button
                  onClick={() => setShowLossVehicles(!showLossVehicles)}
                  className="w-auto px-3 py-[4.74px] rounded-lg outline outline-1 outline-offset-[-1px] outline-stone-300 inline-flex justify-center items-center gap-2 bg-white hover:bg-stone-50 cursor-pointer transition-colors"
                >
                  <div
                    className={`size-6 relative overflow-hidden transition-transform duration-200 ${showLossVehicles ? "" : "rotate-180"}`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M18.4249 13.729C19.4989 15.033 18.5699 17 16.8799 17H7.11986C5.42986 17 4.50186 15.033 5.57586 13.729L10.4569 7.802C10.6445 7.57411 10.8802 7.39059 11.1472 7.26459C11.4141 7.13859 11.7057 7.07325 12.0009 7.07325C12.2961 7.07325 12.5876 7.13859 12.8545 7.26459C13.1215 7.39059 13.3572 7.57411 13.5449 7.802L18.4249 13.729Z"
                        fill="black"
                      />
                    </svg>
                  </div>
                  <div className="justify-start text-red-900 text-base font-medium font-['Inter'] leading-6">
                    View all
                  </div>
                </button>
              </div>
            </div>

            {showLossVehicles && (
              <div className="mt-4 pt-4 border-t border-rose-200 flex flex-col gap-3">
                <div className="overflow-y-auto max-h-[400px] scrollbar-hide">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {dashboardData.lossVehicles.map((vehicle) => (
                      <div
                        key={vehicle.id}
                        className="bg-white border border-rose-100 rounded-xl p-4 flex items-center gap-4 shadow-sm hover:shadow transition-shadow"
                      >
                        <div className="w-20 h-16 bg-transparent rounded-md overflow-hidden flex-shrink-0 flex items-center justify-center relative">
                          {vehicle.imageUrl ? (
                            <img
                              src={vehicle.imageUrl}
                              alt={vehicle.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-[10px] text-gray-400 font-bold uppercase text-center leading-tight">
                              No
                              <br />
                              Image
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="text-gray-900 font-bold text-[20px] truncate">
                            {vehicle.name}
                          </span>
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className="bg-slate-100 text-slate-600 text-[12px] px-1.5 py-0.5 rounded font-mono border border-slate-200">
                              #{vehicle.id}
                            </span>
                            <span className="text-md text-gray-500 font-medium truncate">
                              {vehicle.cityText || "Unmapped"}
                            </span>
                          </div>
                          {/* Plate number */}
                          <div className="mt-0.5">
                            <span className="text-[14px] font-mono text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-200">
                              {vehicle.licensePlate || "N/A"}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end">
                          <span className="text-[12px] uppercase text-rose-400 font-bold tracking-wide">
                            Revenue
                          </span>
                          <span className="text-rose-600 text-md font-black">
                            ₹0
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {dashboardData.topPerformers.length > 0 && (
          <div className="bg-white p-2 rounded-3xl shadow-[0px_2px_4px_rgba(0,0,0,0.16)] border border-gray-200">
            <div className="px-5 py-5 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 relative overflow-hidden">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M15.4772 12.89L16.9922 21.416C17.0092 21.5164 16.9951 21.6196 16.9519 21.7118C16.9086 21.8039 16.8382 21.8807 16.7502 21.9318C16.6621 21.9829 16.5605 22.0059 16.459 21.9977C16.3576 21.9895 16.261 21.9506 16.1822 21.886L12.6022 19.199C12.4294 19.0699 12.2195 19.0001 12.0037 19.0001C11.788 19.0001 11.5781 19.0699 11.4052 19.199L7.81924 21.885C7.74056 21.9494 7.6441 21.9884 7.54273 21.9966C7.44137 22.0048 7.33991 21.9818 7.2519 21.9309C7.16389 21.8799 7.09352 21.8033 7.05016 21.7113C7.0068 21.6194 6.99253 21.5163 7.00924 21.416L8.52324 12.89"
                      stroke="#FE9A00"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 14C15.3137 14 18 11.3137 18 8C18 4.68629 15.3137 2 12 2C8.68629 2 6 4.68629 6 8C6 11.3137 8.68629 14 12 14Z"
                      stroke="#FE9A00"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h3 className="text-gray-900 text-lg font-bold leading-7">
                  Top Performers
                </h3>
              </div>
            </div>

            <div className="flex flex-col gap-4 p-0">
              {dashboardData.topPerformers.map((unit, index) => (
                <div
                  key={unit.id}
                  className={`px-5 py-5 bg-white flex items-center justify-between ${
                    index < dashboardData.topPerformers.length - 1
                      ? "border-b border-slate-100"
                      : ""
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={`
                        w-16 h-16 rounded-full flex items-center justify-center
                        shadow-[inset_0px_8px_8px_rgba(0,0,0,0.25)]
                        border-[3.2px] border-white
                        ${index === 0 ? "bg-amber-500" : index === 1 ? "bg-slate-400" : "bg-amber-700"}
                      `}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M12 16C6.23997 16 5.21997 10.26 5.03997 5.70598C4.98897 4.43998 4.96397 3.80598 5.43997 3.22098C5.91497 2.63498 6.48397 2.53898 7.62297 2.34698C9.06981 2.10998 10.5339 1.99391 12 1.99998C13.784 1.99998 15.253 2.15698 16.377 2.34698C17.516 2.53898 18.085 2.63498 18.561 3.22098C19.037 3.80698 19.011 4.43998 18.961 5.70598C18.781 10.26 17.761 16 12.001 16H12Z"
                          stroke="black"
                          strokeWidth="1.5"
                        />
                        <path
                          d="M19 5L19.949 5.316C20.939 5.646 21.434 5.811 21.717 6.204C22 6.597 22 7.12 22 8.162V8.235C22 9.095 22 9.526 21.793 9.878C21.586 10.23 21.209 10.439 20.457 10.858L17.5 12.5M5 5L4.051 5.316C3.061 5.646 2.566 5.811 2.283 6.204C2 6.597 2 7.12 2 8.162V8.235C2 9.095 2 9.526 2.207 9.878C2.414 10.23 2.791 10.439 3.543 10.858L6.5 12.5"
                          stroke="black"
                          strokeWidth="1.5"
                        />
                        <path
                          d="M12 16V19"
                          stroke="black"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M15.5 22H8.5L8.84 20.304C8.88529 20.0774 9.00768 19.8735 9.18634 19.7269C9.365 19.5803 9.58891 19.5001 9.82 19.5H14.18C14.4111 19.5001 14.635 19.5803 14.8137 19.7269C14.9923 19.8735 15.1147 20.0774 15.16 20.304L15.5 22Z"
                          stroke="black"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M18 22H6"
                          stroke="black"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-1 max-w-[450px]">
                    <div className="w-[164px] h-[76px] relative bg-white overflow-hidden flex items-center justify-center">
                      {unit.imageUrl ? (
                        <img
                          src={unit.imageUrl}
                          alt={unit.name}
                          className="w-[148px] h-[69px] absolute left-2 top-0.5 object-contain"
                        />
                      ) : (
                        <span className="text-[10px] text-gray-400 font-bold uppercase border border-dashed border-gray-300 w-[148px] h-[69px] flex items-center justify-center rounded">
                          No Asset Image
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col gap-2">
                      <div className="text-gray-900 text-lg font-bold leading-[22.5px] max-w-[200px] truncate">
                        {unit.name}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="h-[26px] px-1 py-1 bg-slate-200 rounded-md border border-slate-300 flex items-center justify-center">
                          <span className="text-[#121212] text-sm font-medium font-['Outfit'] leading-[16.5px]">
                            {unit.licensePlate}
                          </span>
                        </div>
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                        <span className="text-gray-400 text-sm font-semibold capitalize tracking-[0.6px]">
                          {unit.cityText || "Unmapped"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="w-[58px] h-4 rotate-90 hidden lg:block">
                    <div className="w-full h-[1px] bg-gray-300"></div>
                  </div>

                  <div className="flex-1 flex justify-end items-center gap-12 lg:gap-24 overflow-x-auto px-4">
                    <div className="flex flex-col items-end gap-2">
                      <span className="text-gray-400 text-sm font-semibold uppercase">
                        Revenue
                      </span>
                      <span className="text-orange-600 text-2xl font-black leading-8 text-right whitespace-nowrap">
                        {formatCurrency(unit.totalRevenue)}
                      </span>
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      <span className="text-gray-400 text-sm font-semibold uppercase">
                        ADR/Day
                      </span>
                      <span className="text-orange-600 text-2xl font-black leading-8 text-right whitespace-nowrap">
                        {formatCurrency(unit.averageRevenuePerDay)}
                      </span>
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      <span className="text-gray-400 text-sm font-semibold uppercase">
                        Bookings
                      </span>
                      <span className="text-orange-600 text-2xl font-black leading-8 text-right whitespace-nowrap">
                        {unit.bookingCount}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="w-full bg-white rounded-3xl border border-[#E5E7EB] p-6 shadow-sm flex flex-col gap-6">
          {/* Visual / Numerical Toggle */}
          <div className="w-full flex flex-col gap-6 items-center">
            <div
              className="flex items-center justify-center p-[0.8px]"
              style={{
                width: "414px",
                height: "61.6px",
                // Using a large value for a pill-shaped border-radius
                borderRadius: "9999px",
                border: "0.8px solid #B5BECB",
                background: "rgba(177, 184, 198, 0.22)",
              }}
            >
              {/* Buttons now use valid Tailwind sizing classes */}
              <button
                onClick={() => setViewMode("visual")}
                className={`flex items-center justify-center gap-1.5 rounded-full text-[14px] font-bold transition w-[200px] h-[48px] ${
                  viewMode === "visual"
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "text-gray-500 hover:text-gray-900"
                }`}
              >
                <BarChart fontSize="inherit" /> VISUAL ANALYTICS
              </button>

              <button
                onClick={() => setViewMode("numerical")}
                className={`flex items-center justify-center gap-1.5 rounded-full text-[14px] font-bold transition w-[200px] h-[48px] ${
                  viewMode === "numerical"
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "text-gray-500 hover:text-gray-900"
                }`}
              >
                <ListAlt fontSize="inherit" /> NUMERICAL DATA
              </button>
            </div>
          </div>

          {viewMode === "visual" && (
            <div className="flex flex-col gap-5 animate-fade-in pb-8">
              {/* Local keyframes for smooth bar/chart loading animations */}
              <style>{`
        @keyframes growUp { from { height: 0px; opacity: 0.5; } to { opacity: 1; } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes growRight { from { width: 0px; opacity: 0.5; } to { opacity: 1; } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-grow-up { animation: growUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-grow-right { animation: growRight 1s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
        .animate-fade-in { animation: fadeIn 0.5s ease-out forwards; }
        .donut-path { transition: transform 0.2s ease, opacity 0.2s ease; transform-origin: 80px 80px; }
        .donut-path.dimmed { opacity: 0.22; }
        .donut-path.highlighted { transform: scale(1.05); }
      `}</style>

              {/* --- ROW 1: Grouped Bar Chart (Full Width) --- */}
              <div className="bg-white p-6 rounded-2xl shadow-sm outline outline-[0.80px] outline-gray-200 hover:border-gray-300 transition-colors">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-gray-900 text-sm font-medium">
                    Revenue target vs achievement
                  </h4>
                  <div className="flex items-center gap-3 text-[11px] text-gray-500 font-medium select-none">
                    <div className="flex items-center gap-1.5 cursor-pointer hover:text-gray-900 transition-colors">
                      <div className="w-2.5 h-2.5 bg-[#6B8FD4] rounded-sm" />
                      <span>Achieved</span>
                    </div>
                    <div className="flex items-center gap-1.5 cursor-pointer hover:text-gray-900 transition-colors">
                      <div className="w-2.5 h-2.5 bg-[#1E3A6E] rounded-sm" />
                      <span>Target</span>
                    </div>
                  </div>
                </div>

                {/* Horizontal scrollable container with hidden scrollbar */}
                <div className="overflow-x-auto scrollbar-hide">
                  <div className="relative h-[510px] min-w-[1560px] w-full mt-2">
                    {/* Y-axis labels */}
                    <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-[11px] text-gray-400 font-medium py-1 z-20 bg-white pr-4">
                      <span>₹10k</span>
                      <span>₹8k</span>
                      <span>₹6k</span>
                      <span>₹4k</span>
                      <span>₹2k</span>
                      <span>₹0</span>
                    </div>

                    {/* Horizontal grid lines */}
                    <div className="absolute left-14 right-0 top-0 h-full flex flex-col justify-between py-1.5 pointer-events-none">
                      {[1, 2, 3, 4, 5, 6].map((i) => (
                        <div
                          key={i}
                          className="w-full border-b border-gray-100/80"
                        />
                      ))}
                    </div>

                    {/* Grouped Bars */}
                    <div className="ml-14 h-full flex items-end justify-start gap-3 px-4 relative z-10">
                      {dashboardData.revenueList
                        .filter((v) => v.totalRevenue > 0)
                        .sort((a, b) => b.totalRevenue - a.totalRevenue)
                        .map((v, index) => {
                          const maxVal =
                            dashboardData.revenueList.reduce(
                              (max, item) => Math.max(max, item.totalRevenue),
                              0,
                            ) || 10000;
                          const targetVal = v.totalRevenue * 0.8;

                          // Map values to pixel height (max 440px)
                          const achHeight = Math.max(
                            (v.totalRevenue / maxVal) * 440,
                            4,
                          );
                          const tgtHeight = Math.max(
                            (targetVal / maxVal) * 440,
                            4,
                          );

                          return (
                            <div
                              key={v.id}
                              className="flex-1 min-w-[100px] max-w-[140px] flex flex-col items-center gap-2.5 h-full justify-end group cursor-pointer relative"
                            >
                              {/* CSS-based floating Tooltip - fixed positioning */}
                              <div className="absolute bottom-[calc(100%-100px)] left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 group-hover:-translate-y-4 transition-all duration-200 pointer-events-none z-50 flex flex-col items-center">
                                <div className="bg-white border border-gray-200 text-[11px] p-3 rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.15)] whitespace-nowrap min-w-[130px] max-w-[200px]">
                                  <strong className="block text-gray-900 font-medium mb-1.5 border-b border-gray-100 pb-1.5 text-center">
                                    {v.name}
                                  </strong>
                                  <div className="flex justify-between gap-5 mt-1">
                                    <span className="text-gray-500">
                                      Achieved
                                    </span>
                                    <span className="font-medium text-[#6B8FD4]">
                                      {formatCurrency(v.totalRevenue)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between gap-5 mt-1">
                                    <span className="text-gray-500">
                                      Target
                                    </span>
                                    <span className="font-medium text-[#1E3A6E]">
                                      {formatCurrency(targetVal)}
                                    </span>
                                  </div>
                                  {/* Small triangle pointer */}
                                  <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-r border-b border-gray-200 rotate-45"></div>
                                </div>
                              </div>

                              {/* The Bars */}
                              <div className="flex items-end justify-center w-full h-[440px] gap-2">
                                <div
                                  className="w-full max-w-[30px] bg-[#6B8FD4] rounded-t-[4px] animate-grow-up"
                                  style={{
                                    height: `${achHeight}px`,
                                    animationDelay: `${index * 80}ms`,
                                  }}
                                />
                                <div
                                  className="w-full max-w-[30px] bg-[#1E3A6E] rounded-t-[4px] animate-grow-up"
                                  style={{
                                    height: `${tgtHeight}px`,
                                    animationDelay: `${index * 80 + 40}ms`,
                                  }}
                                />
                              </div>
                              {/* X-Axis Label */}
                              <span className="text-[11px] text-gray-500 truncate w-full text-center px-1">
                                {v.name?.substring(0, 8) || "N/A"}
                              </span>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>

              {/* --- ROW 2: Donut Chart & Horizontal Bars --- */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* City Contribution Donut */}
                <div className="bg-white p-5 rounded-2xl shadow-sm outline outline-[0.80px] outline-gray-200 hover:border-gray-300 transition-colors flex flex-col">
                  <h4 className="text-gray-900 text-sm font-medium mb-2">
                    City contribution
                  </h4>

                  <div className="flex flex-col items-center gap-4 mt-2">
                    {/* SVG Donut */}
                    <div className="relative w-[320px] h-[320px] flex-shrink-0">
                      {(() => {
                        // Combine cities - normalize names
                        const cityRevenue = {};
                        dashboardData.revenueList.forEach((v) => {
                          let city = v.cityText || "Unknown";
                          // Normalize city names
                          if (city === "Bengaluru") city = "Bangalore";
                          if (city === "Mysore" || city === "Mysure")
                            city = "Mysuru";
                          cityRevenue[city] =
                            (cityRevenue[city] || 0) + v.totalRevenue;
                        });
                        const total =
                          Object.values(cityRevenue).reduce(
                            (a, b) => a + b,
                            0,
                          ) || 1;
                        let startAngle = -Math.PI / 2;

                        const cx = 160,
                          cy = 160,
                          r = 120,
                          ir = 72;

                        return (
                          <svg
                            viewBox="0 0 320 320"
                            className="w-[320px] h-[320px] overflow-visible block"
                          >
                            {Object.entries(cityRevenue).map(
                              ([city, revenue]) => {
                                const frac = revenue / total;
                                const sweep = frac * 2 * Math.PI;

                                const x1 = cx + r * Math.cos(startAngle);
                                const y1 = cy + r * Math.sin(startAngle);
                                const x2 =
                                  cx + r * Math.cos(startAngle + sweep);
                                const y2 =
                                  cy + r * Math.sin(startAngle + sweep);

                                const xi1 = cx + ir * Math.cos(startAngle);
                                const yi1 = cy + ir * Math.sin(startAngle);
                                const xi2 =
                                  cx + ir * Math.cos(startAngle + sweep);
                                const yi2 =
                                  cy + ir * Math.sin(startAngle + sweep);

                                const la = sweep > Math.PI ? 1 : 0;
                                const d = `M${xi1.toFixed(2)},${yi1.toFixed(2)} A${ir},${ir} 0 ${la} 1 ${xi2.toFixed(2)},${yi2.toFixed(2)} L${x2.toFixed(2)},${y2.toFixed(2)} A${r},${r} 0 ${la} 0 ${x1.toFixed(2)},${y1.toFixed(2)} Z`;

                                startAngle += sweep;
                                const segmentColor =
                                  cityColors[city] || "#94A3B8";

                                const isHovered = hoveredCity === city;
                                const isDimmed =
                                  hoveredCity && hoveredCity !== city;

                                return (
                                  <path
                                    key={city}
                                    d={d}
                                    fill={segmentColor}
                                    stroke="white"
                                    strokeWidth="2"
                                    className={`donut-path cursor-pointer ${isHovered ? "highlighted" : ""} ${isDimmed ? "dimmed" : ""}`}
                                    onMouseEnter={() => setHoveredCity(city)}
                                    onMouseLeave={() => setHoveredCity(null)}
                                  />
                                );
                              },
                            )}
                          </svg>
                        );
                      })()}

                      {/* Center Text */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        {(() => {
                          const cityRevenue = {};
                          dashboardData.revenueList.forEach((v) => {
                            let city = v.cityText || "Unknown";
                            if (city === "Bengaluru") city = "Bangalore";
                            if (city === "Mysore" || city === "Mysure")
                              city = "Mysuru";
                            cityRevenue[city] =
                              (cityRevenue[city] || 0) + v.totalRevenue;
                          });
                          const total =
                            Object.values(cityRevenue).reduce(
                              (a, b) => a + b,
                              0,
                            ) || 1;

                          let centerVal = "Total";
                          let centerLabel = "share";

                          if (hoveredCity && cityRevenue[hoveredCity]) {
                            centerVal =
                              Math.round(
                                (cityRevenue[hoveredCity] / total) * 100,
                              ) + "%";
                            centerLabel = hoveredCity;
                          }

                          return (
                            <>
                              <div className="text-[26px] font-medium text-gray-900 transition-all duration-300">
                                {centerVal}
                              </div>
                              <div className="text-[14px] text-gray-400 uppercase tracking-widest font-medium">
                                {centerLabel}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Interactive Legend */}
                    <div className="w-full flex flex-col gap-1 border-t border-gray-100 pt-3">
                      {(() => {
                        const cityRevenue = {};
                        dashboardData.revenueList.forEach((v) => {
                          let city = v.cityText || "Unknown";
                          if (city === "Bengaluru") city = "Bangalore";
                          if (city === "Mysore" || city === "Mysure")
                            city = "Mysuru";
                          cityRevenue[city] =
                            (cityRevenue[city] || 0) + v.totalRevenue;
                        });
                        const total =
                          Object.values(cityRevenue).reduce(
                            (a, b) => a + b,
                            0,
                          ) || 1;

                        return Object.entries(cityRevenue).map(
                          ([city, revenue]) => {
                            const isHovered = hoveredCity === city;
                            return (
                              <div
                                key={city}
                                className={`flex justify-between items-center px-2 py-1.5 rounded-md cursor-pointer text-base transition-colors ${isHovered ? "bg-gray-50" : "hover:bg-gray-50"}`}
                                onMouseEnter={() => setHoveredCity(city)}
                                onMouseLeave={() => setHoveredCity(null)}
                              >
                                <div className="flex items-center gap-2 text-gray-600">
                                  <div
                                    className="w-2.5 h-2.5 rounded-[2px]"
                                    style={{
                                      backgroundColor:
                                        cityColors[city] || "#94A3B8",
                                    }}
                                  />
                                  <span>{city}</span>
                                </div>
                                <div className="flex items-center">
                                  <span
                                    className={`text-[18px] text-gray-400 mr-2 transition-opacity duration-200 ${isHovered ? "opacity-100" : "opacity-0"}`}
                                  >
                                    {formatCurrency(revenue)}
                                  </span>
                                  <span className="font-medium text-gray-900 text-base">
                                    {((revenue / total) * 100).toFixed(1)}%
                                  </span>
                                </div>
                              </div>
                            );
                          },
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Model-wise Yield Distribution H-Bars */}
                <div className="bg-white p-5 rounded-2xl shadow-sm outline outline-[0.80px] outline-gray-200 hover:border-gray-300 transition-colors">
                  <h4 className="text-gray-900 text-sm font-medium mb-3">
                    Model-wise Revenue
                  </h4>

                  {/* Scrollable container with hidden scrollbar */}
                  <div className="max-h-[480px] overflow-y-auto scrollbar-hide">
                    <div className="flex flex-col gap-2 pr-2">
                      {dashboardData.revenueList
                        .filter((v) => v.totalRevenue > 0)
                        .sort((a, b) => b.totalRevenue - a.totalRevenue)
                        .map((v, idx) => {
                          const maxRevenue =
                            dashboardData.revenueList.reduce(
                              (max, item) => Math.max(max, item.totalRevenue),
                              0,
                            ) || 1;
                          const pct = Math.round(
                            (v.totalRevenue / maxRevenue) * 100,
                          );
                          const hbarColors = [
                            "#4F4EC5",
                            "#3AB09E",
                            "#D4A820",
                            "#C94B4A",
                            "#6A8B63",
                            "#8B5CF6",
                            "#EC4899",
                            "#F59E0B",
                            "#10B981",
                            "#6366F1",
                          ];

                          return (
                            <div
                              key={v.id}
                              className="p-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex justify-between items-baseline mb-1.5">
                                <span className="text-base font-medium text-gray-900 truncate max-w-[180px]">
                                  {v.name}
                                </span>
                                <span className="text-[14px] text-gray-500 flex-shrink-0">
                                  {formatCurrency(v.totalRevenue)}
                                </span>
                              </div>
                              <div className="h-[7px] bg-gray-100 rounded-full overflow-hidden relative">
                                <div
                                  className="h-full rounded-full animate-grow-right"
                                  style={{
                                    width: `${Math.max(pct, 2)}%`,
                                    backgroundColor:
                                      hbarColors[idx % hbarColors.length],
                                    animationDelay: `${idx * 40}ms`,
                                  }}
                                />
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>

              {/* --- ROW 3: Occupancy Rate by Node --- */}
              <div className="bg-white p-5 rounded-2xl shadow-sm outline outline-[0.80px] outline-gray-200 hover:border-gray-300 transition-colors w-full">
                <h4 className="text-gray-900 text-sm font-medium mb-3">
                  Occupancy rate by Vehicle (%)
                </h4>

                {/* Scrollable container with hidden scrollbar */}
                <div className="max-h-[400px] overflow-y-auto scrollbar-hide">
                  <div className="flex flex-col gap-2 pr-2">
                    {dashboardData.revenueList.map((v, idx) => {
                      const occ = Math.min(
                        Math.round(((v.totalDaysOperated || 0) / 30) * 100),
                        100,
                      );

                      // Color logic mapping
                      let occCol = "#C94B4A"; // Red (< 50)
                      if (occ >= 80)
                        occCol = "#3AB09E"; // Green
                      else if (occ >= 50) occCol = "#D4A820"; // Yellow

                      return (
                        <div
                          key={v.id}
                          className="flex items-center gap-4 p-2 px-3 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                        >
                          <div className="w-[100px] flex-shrink-0 flex flex-col">
                            <span className="text-[14px] font-medium text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis">
                              {v.licensePlate || "N/A"}
                            </span>
                            <span className="text-[13px] text-gray-400 whitespace-nowrap overflow-hidden text-ellipsis">
                              {v.name}
                            </span>
                          </div>

                          <div className="flex-1 h-[6px] bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full animate-grow-right"
                              style={{
                                width: `${occ}%`,
                                backgroundColor: occCol,
                                animationDelay: `${idx * 60}ms`,
                              }}
                            />
                          </div>

                          <span
                            className="w-[44px] text-right text-[15px] font-medium flex-shrink-0"
                            style={{ color: occCol }}
                          >
                            {occ}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Numerical Mode - Tabs and Tables */}
          {viewMode === "numerical" && (
            <>
              {/* Performance Tiers Section */}
              <div className="self-stretch inline-flex flex-col justify-start items-start gap-6">
                <div className="self-stretch p-6 bg-white rounded-2xl shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10)] shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10)] outline outline-[0.80px] outline-offset-[-0.80px] outline-gray-200 flex flex-col justify-start items-start gap-6">
                  <div className="self-stretch inline-flex justify-between items-start">
                    <div className="justify-start text-gray-900 text-xl font-semibold font-['Inter'] leading-6">
                      Performance Tiers
                    </div>
                  </div>

                  <div className="self-stretch inline-flex justify-center items-start gap-9">
                    {/* Highest Revenue Tier */}
                    <div className="flex-1 flex flex-col justify-start items-start rounded-[22px] border-[1.6px] border-[#DDD] overflow-hidden">
                      <div className="self-stretch h-12 px-4 py-2 bg-[#27A20070] inline-flex justify-start items-center gap-2">
                        <div className="flex-1 self-stretch flex justify-between items-center">
                          <div className="text-gray-900 text-base font-semibold font-['Inter'] leading-5">
                            Highest Revenue
                          </div>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="23"
                            height="24"
                            viewBox="0 0 23 24"
                            fill="none"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M21.611 5.06211C21.406 6.06361 20.2015 6.33511 19.5375 5.67061L18.5975 4.73111C18.4795 4.83778 18.3498 4.95378 18.2085 5.07911C17.2905 5.89611 15.9095 7.12511 14.337 8.97261C13.487 9.97161 11.861 9.96611 11.061 8.85861C10.555 8.15574 10.0295 7.46713 9.485 6.79361L9.403 6.84861C8.078 7.73411 5.023 9.77561 2.946 12.7581C2.87092 12.8659 2.77535 12.9578 2.66474 13.0286C2.55414 13.0994 2.43068 13.1477 2.30139 13.1708C2.0403 13.2175 1.77136 13.1585 1.55375 13.0069C1.33614 12.8552 1.18768 12.6234 1.14103 12.3623C1.09438 12.1012 1.15336 11.8322 1.305 11.6146C3.6045 8.31211 6.935 6.08961 8.2685 5.20061L8.4375 5.08761C9.2775 4.52461 10.374 4.72011 10.991 5.47511C11.5759 6.19594 12.1398 6.93358 12.682 7.68711C12.6855 7.69105 12.6895 7.69443 12.694 7.69711C12.7066 7.70267 12.7202 7.70556 12.734 7.70561C12.776 7.70661 12.802 7.69011 12.814 7.67561C14.4925 5.70361 16.026 4.34061 16.931 3.53661L16.932 3.53611L17.181 3.31411L16.2675 2.40011C15.6025 1.73611 15.8745 0.532112 16.876 0.326612C18.1582 0.0684318 19.4665 -0.0373016 20.7735 0.0116119C21.0754 0.0233437 21.3618 0.148533 21.5754 0.362179C21.7891 0.575824 21.9143 0.8622 21.926 1.16411C21.956 1.87211 21.9595 3.36361 21.611 5.06161M19.5 8.24961C20.023 8.24961 20.445 8.26361 20.7825 8.28461C21.9575 8.35611 22.786 9.24511 22.8675 10.3686C22.934 11.2826 23 12.9036 23 15.7496C23 18.5956 22.934 20.2166 22.8675 21.1306C22.786 22.2541 21.9575 23.1431 20.7825 23.2151C20.3555 23.2397 19.9278 23.2512 19.5 23.2496C19.0722 23.251 18.6445 23.2393 18.2175 23.2146C17.0425 23.1431 16.214 22.2541 16.1325 21.1306C16.066 20.2166 16 18.5956 16 15.7496C16 12.9036 16.066 11.2826 16.1325 10.3686C16.214 9.24511 17.0425 8.35611 18.2175 8.28411C18.6445 8.25955 19.0722 8.24805 19.5 8.24961ZM20.66 10.2806C20.2737 10.2585 19.8869 10.2481 19.5 10.2496C19.0125 10.2496 18.632 10.2626 18.34 10.2806C18.2475 10.2861 18.209 10.3161 18.1915 10.3341C18.1715 10.3541 18.135 10.4046 18.127 10.5141C18.0655 11.3601 18 12.9276 18 15.7496C18 18.5716 18.0655 20.1386 18.127 20.9856C18.135 21.0946 18.172 21.1451 18.1915 21.1651C18.209 21.1831 18.2475 21.2131 18.3395 21.2186C18.6315 21.2366 19.0125 21.2496 19.5 21.2496C19.9875 21.2496 20.368 21.2366 20.66 21.2186C20.7525 21.2131 20.791 21.1831 20.8085 21.1651C20.8285 21.1451 20.865 21.0946 20.873 20.9851C20.9345 20.1386 21 18.5711 21 15.7496C21 12.9281 20.9345 11.3606 20.873 10.5136C20.865 10.4046 20.828 10.3541 20.8085 10.3341C20.791 10.3161 20.752 10.2861 20.66 10.2806ZM3.5 15.2496C4.096 15.2496 4.5535 15.2581 4.9 15.2696C5.878 15.3026 6.799 15.9496 6.9145 17.0896C6.9635 17.5721 7 18.2611 7 19.2496C7 20.2381 6.9635 20.9271 6.9145 21.4096C6.799 22.5496 5.8785 23.1966 4.9005 23.2296C4.43381 23.2441 3.96691 23.2507 3.5 23.2496C2.904 23.2496 2.4465 23.2411 2.1 23.2296C1.1215 23.1966 0.201 22.5496 0.0855 21.4096C0.0365 20.9276 0 20.2386 0 19.2496C0 18.2606 0.0365 17.5721 0.0855 17.0896C0.201 15.9496 1.1215 15.3026 2.0995 15.2696C2.56619 15.2553 3.03309 15.2486 3.5 15.2496ZM4.833 17.2686C4.512 17.2581 4.077 17.2496 3.5 17.2496C2.923 17.2496 2.488 17.2581 2.167 17.2686C2.13624 17.2689 2.10572 17.274 2.0765 17.2836L2.0755 17.2911C2.035 17.6876 2 18.3061 2 19.2496C2 20.1931 2.035 20.8116 2.075 21.2081L2.076 21.2131V21.2156C2.10538 21.2253 2.13607 21.2303 2.167 21.2306C2.488 21.2411 2.923 21.2496 3.5 21.2496C4.077 21.2496 4.512 21.2411 4.833 21.2306C4.86376 21.2303 4.89428 21.2252 4.9235 21.2156L4.9245 21.2081C4.9645 20.8116 5 20.1931 5 19.2496C5 18.3066 4.965 17.6876 4.925 17.2911L4.9235 17.2836C4.89444 17.2741 4.86359 17.269 4.833 17.2686ZM12.794 12.2746C12.4585 12.2596 12.034 12.2496 11.5 12.2496C10.966 12.2496 10.5415 12.2596 10.206 12.2746C9.096 12.3231 8.2045 13.1146 8.107 14.2696C8.05 14.9566 8 16.0396 8 17.7496C8 19.4596 8.0495 20.5431 8.107 21.2296C8.2045 22.3846 9.096 23.1761 10.206 23.2246C10.5415 23.2396 10.966 23.2496 11.5 23.2496C12.034 23.2496 12.4585 23.2396 12.794 23.2246C13.904 23.1761 14.7955 22.3846 14.893 21.2296C14.9505 20.5431 15 19.4596 15 17.7496C15 16.0396 14.9505 14.9561 14.893 14.2696C14.7955 13.1146 13.904 12.3231 12.794 12.2746ZM11.5 14.2496C12.0085 14.2496 12.404 14.2596 12.7065 14.2726C12.8075 14.2771 12.847 14.3086 12.8575 14.3181C12.8655 14.3261 12.8925 14.3531 12.9 14.4381C12.9515 15.0491 13 16.0731 13 17.7496C13 19.4261 12.9515 20.4496 12.9 21.0616C12.893 21.1466 12.8655 21.1731 12.8575 21.1811C12.8475 21.1906 12.8075 21.2221 12.7065 21.2266C12.404 21.2401 12.0085 21.2496 11.5 21.2496C10.9915 21.2496 10.596 21.2396 10.2935 21.2266C10.1925 21.2221 10.153 21.1906 10.1425 21.1811C10.1345 21.1731 10.1075 21.1461 10.1 21.0611C10.0485 20.4501 10 19.4261 10 17.7496C10 16.0731 10.0485 15.0496 10.1 14.4376C10.107 14.3526 10.1345 14.3261 10.1425 14.3181C10.1525 14.3086 10.1925 14.2771 10.2935 14.2726C10.6955 14.2563 11.0977 14.2486 11.5 14.2496Z"
                              fill="black"
                            />
                          </svg>
                        </div>
                      </div>
                      <div className="self-stretch p-2 flex flex-col gap-2">
                        {dashboardData.highestRevenueVehicles?.map((v) => (
                          <div
                            key={v.id}
                            className="self-stretch p-2 border-b border-rose-100 flex justify-between items-center"
                          >
                            <div className="flex flex-col">
                              <span className="text-lg font-medium">
                                {v.name}
                              </span>
                              <span className="text-slate-400 text-sm">
                                {v.licensePlate}
                              </span>
                            </div>
                            <div className="p-2 bg-slate-100 rounded-lg text-neutral-600 font-semibold">
                              {formatCurrency(v.totalRevenue)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Moderate Revenue Tier */}
                    <div className="flex-1 flex flex-col justify-start items-start rounded-[22px] border-[1.6px] border-[#DDD] overflow-hidden">
                      <div className="self-stretch h-12 px-4 py-2 bg-[#D1C70E70] inline-flex justify-start items-center gap-2">
                        <div className="flex-1 self-stretch flex justify-between items-center">
                          <div className="text-gray-900 text-base font-semibold font-['Inter'] leading-5">
                            Moderate Revenue
                          </div>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M15.6 8L18 12L15.6 16M18 12L10.9375 13.2266L7.9375 10.2266L3.9375 13.2266"
                              stroke="black"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      </div>
                      <div className="self-stretch p-2 flex flex-col gap-2">
                        {dashboardData.moderateRevenueVehicles?.map((v) => (
                          <div
                            key={v.id}
                            className="self-stretch p-2 border-b border-rose-100 flex justify-between items-center"
                          >
                            <div className="flex flex-col">
                              <span className="text-lg font-medium">
                                {v.name}
                              </span>
                              <span className="text-slate-400 text-sm">
                                {v.licensePlate}
                              </span>
                            </div>
                            <div className="p-2 bg-slate-100 rounded-lg text-neutral-600 font-semibold">
                              {formatCurrency(v.totalRevenue)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Lowest Revenue Tier */}
                    <div className="flex-1 flex flex-col justify-start items-start rounded-[22px] border-[1.6px] border-[#DDD] overflow-hidden">
                      <div className="self-stretch h-12 px-4 py-2 bg-[#FF525270] inline-flex justify-center items-center gap-2">
                        <div className="flex-1 self-stretch flex justify-between items-center">
                          <div className="text-black text-base font-semibold font-['Inter'] leading-5">
                            Lowest Revenue
                          </div>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M16.2856 18H23.1428V11.1428"
                              stroke="black"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M23.1426 18L15.4569 8.31429C15.2967 8.15723 15.0813 8.06927 14.8569 8.06927C14.6326 8.06927 14.4172 8.15723 14.2569 8.31429L8.31408 12.2571C8.15385 12.4142 7.93844 12.5022 7.71408 12.5022C7.48972 12.5022 7.2743 12.4142 7.11408 12.2571L0.856934 6"
                              stroke="black"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      </div>
                      <div className="self-stretch p-2 flex flex-col gap-2">
                        {dashboardData.lowestRevenueVehicles?.map((v) => (
                          <div
                            key={v.id}
                            className="self-stretch p-2 border-b border-rose-100 flex justify-between items-center"
                          >
                            <div className="flex flex-col">
                              <span className="text-lg font-medium">
                                {v.name}
                              </span>
                              <span className="text-slate-400 text-sm">
                                {v.licensePlate}
                              </span>
                            </div>
                            <div className="p-2 bg-slate-100 rounded-lg text-neutral-600 font-semibold">
                              {formatCurrency(v.totalRevenue)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Add-on Breakdown Section */}
              <div className="w-full inline-flex justify-start items-center gap-6 mt-4">
                <div className="flex-1 p-6 bg-white rounded-2xl shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10)] shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10)] outline outline-[0.80px] outline-offset-[-0.80px] outline-gray-200 inline-flex flex-col justify-start items-start">
                  <div className="w-full pb-4 flex justify-between items-center">
                    <div className="justify-start text-gray-900 text-xl font-semibold font-['Inter'] leading-6">
                      Add-on Breakdown
                    </div>
                    <MetricAIAssistant 
                      metricName="Add-on Revenue Breakdown"
                      metricData={{
                        chargesTotal: dashboardData.chargesTotal,
                        totalAddon: (dashboardData.chargesTotal?.fuel || 0) +
                          (dashboardData.chargesTotal?.downtimeCharges || 0) +
                          (dashboardData.chargesTotal?.extraKm || 0) +
                          (dashboardData.chargesTotal?.extraHrs || 0) +
                          (dashboardData.chargesTotal?.deliveryAndPickup || 0),
                        city: cityFilter,
                        dateRange: selectionRange
                      }}
                      token={token}
                    />
                  </div>
                  <div className="self-stretch flex flex-col justify-start items-start gap-2">
                    <div className="self-stretch h-20 p-2.5 bg-neutral-200/10 rounded-tl-lg rounded-tr-lg inline-flex justify-start items-center gap-4">
                      {/* Fuel Revenue */}
                      <div className="flex-1 inline-flex flex-col justify-start items-start gap-4">
                        <div className="inline-flex justify-center items-center gap-2">
                          <div className="size-6 relative overflow-hidden">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M14 13H16C16.5304 13 17.0391 13.2107 17.4142 13.5858C17.7893 13.9609 18 14.4696 18 15V17C18 17.5304 18.2107 18.0391 18.5858 18.4142C18.9609 18.7893 19.4696 19 20 19C20.5304 19 21.0391 18.7893 21.4142 18.4142C21.7893 18.0391 22 17.5304 22 17V10.002C22.0002 9.73809 21.9482 9.47674 21.8469 9.23303C21.7457 8.98932 21.5972 8.76806 21.41 8.582L18 5M14 21V5C14 4.46957 13.7893 3.96086 13.4142 3.58579C13.0391 3.21071 12.5304 3 12 3H5C4.46957 3 3.96086 3.21071 3.58579 3.58579C3.21071 3.96086 3 4.46957 3 5V21M2 21H15M3 9H14"
                                stroke="#9A9A9A"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <div className="justify-start text-neutral-400 text-base font-medium font-['Inter'] leading-5">
                            Fuel Revenue
                          </div>
                        </div>
                        <div className="self-stretch justify-start text-gray-900 text-xl font-bold font-['Inter'] leading-5">
                          {formatCurrency(
                            dashboardData.chargesTotal?.fuel || 0,
                          )}
                        </div>
                      </div>

                      {/* Downtime Revenue */}
                      <div className="flex-1 inline-flex flex-col justify-start items-start gap-4">
                        <div className="inline-flex justify-center items-center gap-2">
                          <div className="size-6 relative overflow-hidden">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <g clipPath="url(#clip0_813_473)">
                                <path
                                  d="M22.5 18.75V23.25H18"
                                  stroke="#9A9A9A"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M22.5 23.25L17.45 18.2C17.1629 17.9126 16.8033 17.7084 16.4094 17.6089C16.0156 17.5094 15.6021 17.5184 15.213 17.635L9.5 19.161C9.11872 19.263 8.71732 19.2629 8.33608 19.1608C7.95484 19.0586 7.60718 18.858 7.328 18.579L1.5 12.746M3.148 9.73897C2.88223 8.42378 2.97415 7.06138 3.41418 5.79381C3.85422 4.52624 4.62624 3.39994 5.64976 2.53232C6.67329 1.6647 7.91082 1.08756 9.23334 0.861054C10.5559 0.634551 11.9149 0.766988 13.1688 1.24456C14.4227 1.72212 15.5256 2.52733 16.3623 3.57623C17.1991 4.62512 17.7391 5.87929 17.9262 7.20797C18.1132 8.53664 17.9404 9.89116 17.4257 11.1303C16.911 12.3694 16.0733 13.4478 15 14.253"
                                  stroke="#9A9A9A"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M9.05482 5C9.82157 5 10.5569 5.30459 11.0991 5.84675C11.6412 6.38892 11.9458 7.12426 11.9458 7.891C11.9458 8.65774 11.6412 9.39308 11.0991 9.93525C10.5569 10.4774 9.82157 10.782 9.05482 10.782H7.31982L10.7888 14.251M7.31982 5.002H13.6798M9.05482 7.893H13.6798"
                                  stroke="#9A9A9A"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </g>
                              <defs>
                                <clipPath id="clip0_813_473">
                                  <rect width="24" height="24" fill="white" />
                                </clipPath>
                              </defs>
                            </svg>
                          </div>
                          <div className="justify-start text-neutral-400 text-base font-medium font-['Inter'] leading-5">
                            Downtime Revenue
                          </div>
                        </div>
                        <div className="self-stretch justify-start text-gray-900 text-xl font-bold font-['Inter'] leading-5">
                          {formatCurrency(
                            dashboardData.chargesTotal?.downtimeCharges || 0,
                          )}
                        </div>
                      </div>

                      {/* Extra KM */}
                      <div className="flex-1 inline-flex flex-col justify-start items-start gap-4">
                        <div className="inline-flex justify-center items-center gap-2">
                          <div className="size-6 relative overflow-hidden">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M4 19L8 5M16 5L20 19M12 8V6M12 13V11M12 18V16"
                                stroke="#9A9A9A"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <div className="justify-start text-neutral-400 text-base font-medium font-['Inter'] leading-5">
                            Extra KM
                          </div>
                        </div>
                        <div className="self-stretch justify-start text-gray-900 text-xl font-bold font-['Inter'] leading-5">
                          {formatCurrency(
                            dashboardData.chargesTotal?.extraKm || 0,
                          )}
                        </div>
                      </div>

                      {/* Extra Hours */}
                      <div className="flex-1 inline-flex flex-col justify-start items-start gap-4">
                        <div className="inline-flex justify-center items-center gap-2">
                          <div className="size-6 relative overflow-hidden">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <circle
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="#9A9A9A"
                                strokeWidth="2"
                              />
                              <path
                                d="M12 6V12L16 14"
                                stroke="#9A9A9A"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <div className="justify-start text-neutral-400 text-base font-medium font-['Inter'] leading-5">
                            Extra Hours
                          </div>
                        </div>
                        <div className="self-stretch justify-start text-gray-900 text-xl font-bold font-['Inter'] leading-5">
                          {formatCurrency(
                            dashboardData.chargesTotal?.extraHrs || 0,
                          )}
                        </div>
                      </div>

                      {/* Delivery & Pickup */}
                      <div className="flex-1 inline-flex flex-col justify-start items-start gap-4">
                        <div className="inline-flex justify-center items-center gap-2">
                          <div className="size-6 relative overflow-hidden">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M7.25 10C7.25 8.74022 7.75045 7.53204 8.64124 6.64124C9.53204 5.75045 10.7402 5.25 12 5.25C13.2598 5.25 14.468 5.75045 15.3588 6.64124C16.2496 7.53204 16.75 8.74022 16.75 10C16.75 11.2598 16.2496 12.468 15.3588 13.3588C14.468 14.2496 13.2598 14.75 12 14.75C10.7402 14.75 9.53204 14.2496 8.64124 13.3588C7.75045 12.468 7.25 11.2598 7.25 10ZM12 6.75C11.138 6.75 10.3114 7.09241 9.7019 7.7019C9.09241 8.3114 8.75 9.13805 8.75 10C8.75 10.862 9.09241 11.6886 9.7019 12.2981C10.3114 12.9076 11.138 13.25 12 13.25C12.862 13.25 13.6886 12.9076 14.2981 12.2981C14.9076 11.6886 15.25 10.862 15.25 10C15.25 9.13805 14.9076 8.3114 14.2981 7.7019C13.6886 7.09241 12.862 6.75 12 6.75Z"
                                fill="#9A9A9A"
                              />
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M3.52398 8.857C3.69535 6.78397 4.63979 4.85113 6.16988 3.442C7.69997 2.03288 9.70388 1.25046 11.784 1.25H12.216C14.2961 1.25046 16.3 2.03288 17.8301 3.442C19.3602 4.85113 20.3046 6.78397 20.476 8.857C20.6666 11.1663 19.9531 13.4595 18.486 15.253L13.693 21.114C13.4879 21.3648 13.2296 21.567 12.9368 21.7057C12.644 21.8445 12.324 21.9165 12 21.9165C11.676 21.9165 11.356 21.8445 11.0632 21.7057C10.7704 21.567 10.5121 21.3648 10.307 21.114L5.51398 15.253C4.04681 13.4595 3.33334 11.1663 3.52398 8.857ZM11.784 2.75C10.0806 2.75072 8.4397 3.39165 7.18679 4.54565C5.93387 5.69965 5.16046 7.28242 5.01998 8.98C4.86126 10.9019 5.45498 12.8103 6.67598 14.303L11.469 20.165C11.5334 20.2439 11.6146 20.3074 11.7066 20.351C11.7986 20.3947 11.8991 20.4173 12.001 20.4173C12.1028 20.4173 12.2034 20.3947 12.2954 20.351C12.3874 20.3074 12.4685 20.2439 12.533 20.165L17.326 14.303C18.5463 12.81 19.1393 10.9016 18.98 8.98C18.8395 7.28225 18.0659 5.69934 16.8128 4.54531C15.5597 3.39128 13.9185 2.75047 12.215 2.75H11.784Z"
                                fill="#9A9A9A"
                              />
                            </svg>
                          </div>
                          <div className="justify-start text-neutral-400 text-base font-medium font-['Inter'] leading-5">
                            Delivery & Pickup
                          </div>
                        </div>
                        <div className="self-stretch justify-start text-gray-900 text-xl font-bold font-['Inter'] leading-5">
                          {formatCurrency(
                            dashboardData.chargesTotal?.deliveryAndPickup || 0,
                          )}
                        </div>
                      </div>

                      {/* Total Add-on - FIX: Calculate correctly */}
                      <div className="flex-1 p-4 bg-indigo-50 rounded-2xl outline outline-1 outline-offset-[-1px] outline-indigo-100 inline-flex flex-col justify-start items-start gap-4">
                        <div className="inline-flex justify-center items-center gap-2">
                          <div className="justify-start text-indigo-700 text-base font-medium font-['Inter'] leading-5">
                            Total Add-on
                          </div>
                        </div>
                        <div className="justify-start text-indigo-900 text-2xl font-bold font-['Inter'] leading-5">
                          {formatCurrency(
                            (dashboardData.chargesTotal?.fuel || 0) +
                              (dashboardData.chargesTotal?.downtimeCharges ||
                                0) +
                              (dashboardData.chargesTotal?.extraKm || 0) +
                              (dashboardData.chargesTotal?.extraHrs || 0) +
                              (dashboardData.chargesTotal?.deliveryAndPickup ||
                                0),
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="h-12 w-[449px] p-1 bg-gray-50 rounded-2xl inline-flex justify-start items-start gap-2">
                <div
                  onClick={() => setActiveTab("performance")}
                  className={`w-32 self-stretch relative rounded-[10px] cursor-pointer ${
                    activeTab === "performance"
                      ? "bg-white shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10)] shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10)]"
                      : ""
                  }`}
                >
                  <div
                    className={`left-[22px] top-[10.60px] absolute text-center text-sm font-semibold font-['Outfit'] leading-5 ${
                      activeTab === "performance"
                        ? "text-indigo-600"
                        : "text-gray-500"
                    }`}
                  >
                    Performance
                  </div>
                </div>
                <div
                  onClick={() => setActiveTab("bookings")}
                  className={`w-36 self-stretch relative rounded-[10px] cursor-pointer ${
                    activeTab === "bookings"
                      ? "bg-white shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10)] shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10)]"
                      : ""
                  }`}
                >
                  <div
                    className={`left-[24px] top-[10.60px] absolute text-center text-sm font-semibold font-['Outfit'] leading-5 ${
                      activeTab === "bookings"
                        ? "text-indigo-600"
                        : "text-gray-500"
                    }`}
                  >
                    Bookings & Idle
                  </div>
                </div>
                <div
                  onClick={() => setActiveTab("addon")}
                  className={`w-40 self-stretch relative rounded-[10px] cursor-pointer ${
                    activeTab === "addon"
                      ? "bg-white shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10)] shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10)]"
                      : ""
                  }`}
                >
                  <div
                    className={`left-[25px] top-[10.60px] absolute text-center text-sm font-semibold font-['Outfit'] leading-5 ${
                      activeTab === "addon"
                        ? "text-indigo-600"
                        : "text-gray-500"
                    }`}
                  >
                    Add-on Revenue
                  </div>
                </div>
              </div>

              <div className="self-stretch p-5 border-b-[0.80px] border-gray-200 inline-flex justify-between items-center">
                <div className="w-96 h-10 max-w-96 relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z"
                        stroke="#9CA3AF"
                        strokeWidth="1.33333"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M13.9995 13.9995L11.1328 11.1328"
                        stroke="#9CA3AF"
                        strokeWidth="1.33333"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search vehicles by name or plate number..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full h-10 pl-9 pr-4 py-2.5 rounded-[10px] outline outline-[0.80px] outline-offset-[-0.80px] outline-gray-300 text-sm focus:outline-indigo-500 transition-all"
                  />
                </div>
                <div className="pl-4 flex justify-start items-start relative">
                  <button
                    onClick={() => setShowExportMenu(!showExportMenu)}
                    className="px-4 py-2.5 bg-indigo-600 rounded-[10px] flex justify-start items-center gap-2 hover:bg-indigo-700 transition-colors"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M8 2V10M8 10L5.5 7.5M8 10L10.5 7.5"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M2 12V13C2 13.5523 2.44772 14 3 14H13C13.5523 14 14 13.5523 14 13V12"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                    <span className="text-white text-sm font-semibold">
                      Export
                    </span>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M2 4L6 8L10 4"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  {/* Dropdown Menu */}
                  {showExportMenu && (
                    <div className="absolute top-full right-0 mt-1 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-50 min-w-[140px]">
                      <button
                        onClick={exportCSV}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                        >
                          <path
                            d="M7 1.5V8.5M7 8.5L4.5 6M7 8.5L9.5 6"
                            stroke="#374151"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M1.5 10.5V11.5C1.5 12.0523 1.94772 12.5 2.5 12.5H11.5C12.0523 12.5 12.5 12.0523 12.5 11.5V10.5"
                            stroke="#374151"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                        Export CSV
                      </button>
                      <button
                        onClick={exportPDF}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                        >
                          <path
                            d="M1.5 2.5H12.5M1.5 5.5H12.5M1.5 8.5H6.5M8.5 8.5H12.5"
                            stroke="#374151"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                          <path
                            d="M2.5 11.5H11.5"
                            stroke="#374151"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                        Export PDF
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {activeTab === "performance" && (
                <div className="w-full overflow-x-auto border border-[#E5E7EB] rounded-xl">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                        <th
                          className="p-4 cursor-pointer hover:text-indigo-600"
                          onClick={() => requestSort("name")}
                        >
                          Vehicle Name{" "}
                          {sortConfig.key === "name" &&
                            (sortConfig.direction === "asc" ? "↑" : "↓")}
                        </th>
                        <th
                          className="p-4 cursor-pointer hover:text-indigo-600"
                          onClick={() => requestSort("licensePlate")}
                        >
                          License Plate{" "}
                          {sortConfig.key === "licensePlate" &&
                            (sortConfig.direction === "asc" ? "↑" : "↓")}
                        </th>
                        <th className="p-4">Type</th>
                        <th
                          className="p-4 cursor-pointer hover:text-indigo-600"
                          onClick={() => requestSort("status")}
                        >
                          Status{" "}
                          {sortConfig.key === "isTargetMet" &&
                            (sortConfig.direction === "asc" ? "↑" : "↓")}
                        </th>
                        <th
                          className="p-4 cursor-pointer hover:text-indigo-600"
                          onClick={() => requestSort("totalRevenue")}
                        >
                          Total Revenue{" "}
                          {sortConfig.key === "totalRevenue" &&
                            (sortConfig.direction === "asc" ? "↑" : "↓")}
                        </th>
                        <th
                          className="p-4 cursor-pointer hover:text-indigo-600"
                          onClick={() => requestSort("averageRevenuePerDay")}
                        >
                          ADR/Day{" "}
                          {sortConfig.key === "averageRevenuePerDay" &&
                            (sortConfig.direction === "asc" ? "↑" : "↓")}
                        </th>
                        <th
                          className="p-4 cursor-pointer hover:text-indigo-600"
                          onClick={() => requestSort("avgDuration")}
                        >
                          Avg Duration{" "}
                          {sortConfig.key === "avgDuration" &&
                            (sortConfig.direction === "asc" ? "↑" : "↓")}
                        </th>
                        <th
                          className="p-4 cursor-pointer hover:text-indigo-600"
                          onClick={() => requestSort("achivement")}
                        >
                          Target achieved{" "}
                          {sortConfig.key === "achivement" &&
                            (sortConfig.direction === "asc" ? "↑" : "↓")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-sm font-medium text-gray-700 divide-y divide-gray-100">
                      {paginatedTableData.length === 0 ? (
                        <tr>
                          <td
                            colSpan={8}
                            className="text-center p-8 text-gray-400"
                          >
                            No Data recorded for chosen timestamp.
                          </td>
                        </tr>
                      ) : (
                        paginatedTableData.map((row) => (
                          <tr
                            key={row.id}
                            className="hover:bg-slate-50/80 transition duration-150"
                          >
                            <td className="p-4">
                              <div className="flex items-center gap-3 group">
                                <div className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100 font-bold text-xs flex items-center justify-center flex-shrink-0">
                                  {row?.name?.charAt(0) || "V"}
                                </div>
                                <div>
                                  <div className="font-bold text-[15px] text-gray-900 max-w-[180px] truncate flex items-center gap-1">
                                    {row.name || "-"}
                                    <button
                                      onClick={() =>
                                        copyToClipboard(
                                          row.name || "",
                                          `name_${row.id}`,
                                        )
                                      }
                                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 12 12"
                                        fill="none"
                                      >
                                        <path
                                          d="M2 2H7V7H2V2Z"
                                          stroke="#6B7280"
                                          strokeWidth="1"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M5 5H10V10H5V5Z"
                                          stroke="#6B7280"
                                          strokeWidth="1"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    </button>
                                    {copiedField === `name_${row.id}` && (
                                      <span className="text-[8px] text-green-600 font-normal">
                                        ✓
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[12px] text-gray-400 flex items-center gap-0.5 mt-0.5">
                                    <LocationOn sx={{ fontSize: 10 }} />
                                    {row.cityText || "Unmapped"}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-2 group">
                                <span className="font-mono text-[16px] font-bold text-slate-600 bg-slate-100 rounded-md px-2.5 py-1 border border-slate-200 tracking-wider">
                                  {row.licensePlate || "UNREGISTERED"}
                                </span>
                                <button
                                  onClick={() =>
                                    copyToClipboard(
                                      row.licensePlate || "",
                                      `plate_${row.id}`,
                                    )
                                  }
                                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                  >
                                    <path
                                      d="M2 2H7V7H2V2Z"
                                      stroke="#6B7280"
                                      strokeWidth="1"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                    <path
                                      d="M5 5H10V10H5V5Z"
                                      stroke="#6B7280"
                                      strokeWidth="1"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </button>
                                {copiedField === `plate_${row.id}` && (
                                  <span className="text-[8px] text-green-600 font-normal">
                                    ✓
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="p-4 text-gray-600">
                              {row.bodyTypeText || "SUV"}
                            </td>
                            <td className="p-4">
                              <div
                                className={`w-24 px-2 py-1 rounded-lg flex items-center gap-2 ${
                                  row.status ===
                                  Number(vehicleStatus?.inverted?.active)
                                    ? "bg-green-400/25 outline outline-1 outline-green-500/70"
                                    : "bg-sky-300/25 outline outline-1 outline-sky-400/60"
                                }`}
                              >
                                {row.status === 179 ? (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    className="flex-shrink-0"
                                  >
                                    <g clip-path="url(#clip0_829_567)">
                                      <path
                                        d="M6.82867 10.876L4 8.04668L4.94267 7.10402L6.82867 8.98935L10.5993 5.21802L11.5427 6.16135L6.82867 10.876Z"
                                        fill="#1A1A1A"
                                      />
                                      <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M0.666656 8.00008C0.666656 3.95008 3.94999 0.666748 7.99999 0.666748C12.05 0.666748 15.3333 3.95008 15.3333 8.00008C15.3333 12.0501 12.05 15.3334 7.99999 15.3334C3.94999 15.3334 0.666656 12.0501 0.666656 8.00008ZM7.99999 14.0001C7.21206 14.0001 6.43184 13.8449 5.70389 13.5434C4.97594 13.2418 4.3145 12.7999 3.75735 12.2427C3.2002 11.6856 2.75824 11.0241 2.45671 10.2962C2.15518 9.56823 1.99999 8.78801 1.99999 8.00008C1.99999 7.21215 2.15518 6.43193 2.45671 5.70398C2.75824 4.97603 3.2002 4.31459 3.75735 3.75744C4.3145 3.20029 4.97594 2.75833 5.70389 2.4568C6.43184 2.15528 7.21206 2.00008 7.99999 2.00008C9.59129 2.00008 11.1174 2.63222 12.2426 3.75744C13.3678 4.88266 14 6.40878 14 8.00008C14 9.59138 13.3678 11.1175 12.2426 12.2427C11.1174 13.3679 9.59129 14.0001 7.99999 14.0001Z"
                                        fill="#1A1A1A"
                                      />
                                    </g>
                                    <defs>
                                      <clipPath id="clip0_829_567">
                                        <rect
                                          width="16"
                                          height="16"
                                          fill="white"
                                        />
                                      </clipPath>
                                    </defs>
                                  </svg>
                                ) : (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    className="flex-shrink-0"
                                  >
                                    <g clip-path="url(#clip0_829_568)">
                                      <path
                                        d="M8 4L8 8.66667M8 11.3333V12"
                                        stroke="#1A1A1A"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                      />
                                      <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M0.666656 8.00008C0.666656 3.95008 3.94999 0.666748 7.99999 0.666748C12.05 0.666748 15.3333 3.95008 15.3333 8.00008C15.3333 12.0501 12.05 15.3334 7.99999 15.3334C3.94999 15.3334 0.666656 12.0501 0.666656 8.00008ZM7.99999 14.0001C7.21206 14.0001 6.43184 13.8449 5.70389 13.5434C4.97594 13.2418 4.3145 12.7999 3.75735 12.2427C3.2002 11.6856 2.75824 11.0241 2.45671 10.2962C2.15518 9.56823 1.99999 8.78801 1.99999 8.00008C1.99999 7.21215 2.15518 6.43193 2.45671 5.70398C2.75824 4.97603 3.2002 4.31459 3.75735 3.75744C4.3145 3.20029 4.97594 2.75833 5.70389 2.4568C6.43184 2.15528 7.21206 2.00008 7.99999 2.00008C9.59129 2.00008 11.1174 2.63222 12.2426 3.75744C13.3678 4.88266 14 6.40878 14 8.00008C14 9.59138 13.3678 11.1175 12.2426 12.2427C11.1174 13.3679 9.59129 14.0001 7.99999 14.0001Z"
                                        fill="#1A1A1A"
                                      />
                                    </g>
                                    <defs>
                                      <clipPath id="clip0_829_568">
                                        <rect
                                          width="16"
                                          height="16"
                                          fill="white"
                                        />
                                      </clipPath>
                                    </defs>
                                  </svg>
                                )}
                                <span className="text-sm font-semibold text-[#1A1A1A]">
                                  {row.status === 179 ? "Active" : "Inactive"}
                                </span>
                              </div>
                            </td>
                            <td className="p-4 font-black text-gray-900">
                              {formatCurrency(row.totalRevenue || 0)}
                            </td>
                            <td className="p-4 font-medium text-gray-700">
                              {formatCurrency(row.averageRevenuePerDay || 0)}
                            </td>
                            <td className="p-4 font-medium text-gray-700">
                              {row.avgDuration || 0} days
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-1">
                                <div className="w-20 bg-indigo-100 rounded-full h-2">
                                  <div
                                    className="h-2 bg-indigo-600 rounded-full"
                                    style={{
                                      width: `${Math.min(row.achivement || 0, 100)}%`,
                                    }}
                                  />
                                </div>
                                <span className="text-xs font-medium text-neutral-500">
                                  {Math.round(row.achivement || 0)}%
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === "bookings" && (
                <div className="w-full overflow-x-auto border border-[#E5E7EB] rounded-xl">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                        <th
                          className="p-4 cursor-pointer hover:text-indigo-600"
                          onClick={() => requestSort("name")}
                        >
                          Vehicle Name{" "}
                          {sortConfig.key === "name" &&
                            (sortConfig.direction === "asc" ? "↑" : "↓")}
                        </th>
                        <th
                          className="p-4 cursor-pointer hover:text-indigo-600"
                          onClick={() => requestSort("licensePlate")}
                        >
                          License Plate{" "}
                          {sortConfig.key === "licensePlate" &&
                            (sortConfig.direction === "asc" ? "↑" : "↓")}
                        </th>
                        <th
                          className="p-4 cursor-pointer hover:text-indigo-600"
                          onClick={() => requestSort("totalDaysOperated")}
                        >
                          Active Days{" "}
                          {sortConfig.key === "totalDaysOperated" &&
                            (sortConfig.direction === "asc" ? "↑" : "↓")}
                        </th>
                        <th className="p-4">Idle Days</th>
                        <th className="p-4">Occupancy</th>
                        <th className="p-4">18-day status</th>
                        <th
                          className="p-4 cursor-pointer hover:text-indigo-600"
                          onClick={() => requestSort("bookingCount")}
                        >
                          Bookings{" "}
                          {sortConfig.key === "bookingCount" &&
                            (sortConfig.direction === "asc" ? "↑" : "↓")}
                        </th>
                        <th className="p-4">Avg gap(Days)</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm font-medium text-gray-700 divide-y divide-gray-100">
                      {paginatedTableData.length === 0 ? (
                        <tr>
                          <td
                            colSpan={8}
                            className="text-center p-8 text-gray-400"
                          >
                            No Data recorded for chosen timestamp.
                          </td>
                        </tr>
                      ) : (
                        paginatedTableData.map((row) => {
                          const activeDays = row.totalDaysOperated || 0;
                          const idleDays = Math.max(0, 30 - activeDays);
                          const occupancy =
                            activeDays > 0
                              ? Math.round((activeDays / 30) * 100)
                              : 0;
                          const avgGap =
                            row.bookingCount > 0
                              ? (activeDays / row.bookingCount).toFixed(1)
                              : 0;
                          const daysRemaining = row.daysRemaining || 18;

                          let statusLabel = "On track";
                          let statusColor = "#006240"; // Green

                          const daysRun = row.totalDaysOperated || 0;
                          const totalDaysInPeriod = 30; // Since your date range is 1 month
                          const occupancyPercent = Math.round(
                            (daysRun / totalDaysInPeriod) * 100,
                          );

                          if (daysRun === 0) {
                            statusLabel = "Never Used";
                            statusColor = "#AB0000"; // Red
                          } else if (occupancyPercent < 20) {
                            statusLabel = "Rarely Used";
                            statusColor = "#AB0000"; // Red
                          } else if (occupancyPercent < 40) {
                            statusLabel = "Underused";
                            statusColor = "#FF8C00"; // Orange
                          } else if (occupancyPercent < 60) {
                            statusLabel = "Moderately Used";
                            statusColor = "#0D2E9B"; // Blue
                          } else if (occupancyPercent < 80) {
                            statusLabel = "Well Used";
                            statusColor = "#006240"; // Green
                          } else if (occupancyPercent >= 80) {
                            statusLabel = "Highly Utilized";
                            statusColor = "#006240"; // Green
                          }

                          return (
                            <tr
                              key={row.id}
                              className="hover:bg-slate-50/80 transition duration-150"
                            >
                              <td className="p-4">
                                <div className="flex items-center gap-3 group">
                                  <div className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100 font-bold text-xs flex items-center justify-center flex-shrink-0">
                                    {row?.name?.charAt(0) || "V"}
                                  </div>
                                  <div>
                                    <div className="font-bold text-[15px] text-gray-900 max-w-[180px] truncate flex items-center gap-1">
                                      {row.name || "-"}
                                      <button
                                        onClick={() =>
                                          copyToClipboard(
                                            row.name || "",
                                            `name_${row.id}`,
                                          )
                                        }
                                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                                      >
                                        <svg
                                          width="16"
                                          height="16"
                                          viewBox="0 0 12 12"
                                          fill="none"
                                        >
                                          <path
                                            d="M2 2H7V7H2V2Z"
                                            stroke="#6B7280"
                                            strokeWidth="1"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                          <path
                                            d="M5 5H10V10H5V5Z"
                                            stroke="#6B7280"
                                            strokeWidth="1"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      </button>
                                      {copiedField === `name_${row.id}` && (
                                        <span className="text-[8px] text-green-600 font-normal">
                                          ✓
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-[11px] text-gray-400 flex items-center gap-0.5 mt-0.5">
                                      <LocationOn sx={{ fontSize: 10 }} />
                                      {row.cityText || "Unmapped"}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="p-4">
                                <div className="flex items-center gap-2 group">
                                  <span className="font-mono text-[16px] font-bold text-slate-600 bg-slate-100 rounded-md px-2.5 py-1 border border-slate-200 tracking-wider">
                                    {row.licensePlate || "UNREGISTERED"}
                                  </span>
                                  <button
                                    onClick={() =>
                                      copyToClipboard(
                                        row.licensePlate || "",
                                        `plate_${row.id}`,
                                      )
                                    }
                                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <svg
                                      width="16"
                                      height="16"
                                      viewBox="0 0 12 12"
                                      fill="none"
                                    >
                                      <path
                                        d="M2 2H7V7H2V2Z"
                                        stroke="#6B7280"
                                        strokeWidth="1"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M5 5H10V10H5V5Z"
                                        stroke="#6B7280"
                                        strokeWidth="1"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </button>
                                  {copiedField === `plate_${row.id}` && (
                                    <span className="text-[8px] text-green-600 font-normal">
                                      ✓
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="p-4 font-semibold text-gray-700">
                                {activeDays}
                              </td>
                              <td className="p-4 font-medium text-gray-600">
                                {idleDays}
                              </td>
                              <td className="p-4 font-medium text-gray-700">
                                {occupancy}%
                              </td>
                              <td className="p-4">
                                <div
                                  className={`w-28 px-2.5 py-1 rounded-lg flex items-center gap-2 bg-${statusColor === "#006240" ? "emerald" : statusColor === "#0D2E9B" ? "blue" : "red"}-50 outline outline-[0.80px] outline-offset-[-0.80px] outline-stone-200`}
                                >
                                  <div
                                    className={`w-2 h-1.5 rounded-full`}
                                    style={{ backgroundColor: statusColor }}
                                  />
                                  <span
                                    className="text-xs font-medium capitalize"
                                    style={{ color: statusColor }}
                                  >
                                    {statusLabel}
                                  </span>
                                </div>
                              </td>
                              <td className="p-4 font-bold text-indigo-600">
                                {row.bookingCount}
                              </td>
                              <td className="p-4 font-medium text-gray-700">
                                {avgGap}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === "addon" && (
                <div className="w-full overflow-x-auto border border-[#E5E7EB] rounded-xl">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                        <th
                          className="p-4 cursor-pointer hover:text-indigo-600"
                          onClick={() => requestSort("name")}
                        >
                          Vehicle Name{" "}
                          {sortConfig.key === "name" &&
                            (sortConfig.direction === "asc" ? "↑" : "↓")}
                        </th>
                        <th
                          className="p-4 cursor-pointer hover:text-indigo-600"
                          onClick={() => requestSort("licensePlate")}
                        >
                          License Plate{" "}
                          {sortConfig.key === "licensePlate" &&
                            (sortConfig.direction === "asc" ? "↑" : "↓")}
                        </th>
                        <th
                          className="p-4 cursor-pointer hover:text-indigo-600"
                          onClick={() => requestSort("fuel")}
                        >
                          Fuel{" "}
                          {sortConfig.key === "fuel" &&
                            (sortConfig.direction === "asc" ? "↑" : "↓")}
                        </th>
                        <th
                          className="p-4 cursor-pointer hover:text-indigo-600"
                          onClick={() => requestSort("extraKm")}
                        >
                          Extra KM{" "}
                          {sortConfig.key === "extraKm" &&
                            (sortConfig.direction === "asc" ? "↑" : "↓")}
                        </th>
                        <th
                          className="p-4 cursor-pointer hover:text-indigo-600"
                          onClick={() => requestSort("extraHrs")}
                        >
                          Extra Hours{" "}
                          {sortConfig.key === "extraHrs" &&
                            (sortConfig.direction === "asc" ? "↑" : "↓")}
                        </th>
                        <th
                          className="p-4 cursor-pointer hover:text-indigo-600"
                          onClick={() =>
                            requestSort("deliveryAndPickupCharges")
                          }
                        >
                          Delivery/Pickup{" "}
                          {sortConfig.key === "deliveryAndPickupCharges" &&
                            (sortConfig.direction === "asc" ? "↑" : "↓")}
                        </th>
                        <th
                          className="p-4 cursor-pointer hover:text-indigo-600"
                          onClick={() => requestSort("downtimeCharges")}
                        >
                          Downtime{" "}
                          {sortConfig.key === "downtimeCharges" &&
                            (sortConfig.direction === "asc" ? "↑" : "↓")}
                        </th>
                        <th className="p-4">Total Add-on</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm font-medium text-gray-700 divide-y divide-gray-100">
                      {paginatedTableData.length === 0 ? (
                        <tr>
                          <td
                            colSpan={8}
                            className="text-center p-8 text-gray-400"
                          >
                            No Data recorded for chosen timestamp.
                          </td>
                        </tr>
                      ) : (
                        paginatedTableData.map((row) => {
                          // FIX: Calculate total add-on from ALL fields
                          const totalAddon =
                            (row.fuel || 0) +
                            (row.extraKm || 0) +
                            (row.extraHrs || 0) +
                            (row.deliveryAndPickupCharges || 0) +
                            (row.downtimeCharges || 0);
                          return (
                            <tr
                              key={row.id}
                              className="hover:bg-slate-50/80 transition duration-150"
                            >
                              <td className="p-4">
                                <div className="flex items-center gap-3 group">
                                  <div className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100 font-bold text-xs flex items-center justify-center flex-shrink-0">
                                    {row?.name?.charAt(0) || "V"}
                                  </div>
                                  <div>
                                    <div className="font-bold text-[15px] text-gray-900 max-w-[180px] truncate flex items-center gap-1">
                                      {row.name || "-"}
                                      <button
                                        onClick={() =>
                                          copyToClipboard(
                                            row.name || "",
                                            `name_${row.id}`,
                                          )
                                        }
                                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                                      >
                                        <svg
                                          width="16"
                                          height="16"
                                          viewBox="0 0 12 12"
                                          fill="none"
                                        >
                                          <path
                                            d="M2 2H7V7H2V2Z"
                                            stroke="#6B7280"
                                            strokeWidth="1"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                          <path
                                            d="M5 5H10V10H5V5Z"
                                            stroke="#6B7280"
                                            strokeWidth="1"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      </button>
                                      {copiedField === `name_${row.id}` && (
                                        <span className="text-[8px] text-green-600 font-normal">
                                          ✓
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-[11px] text-gray-400 flex items-center gap-0.5 mt-0.5">
                                      <LocationOn sx={{ fontSize: 10 }} />
                                      {row.cityText || "Unmapped"}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="p-4">
                                <div className="flex items-center gap-2 group">
                                  <span className="font-mono text-[16px] font-bold text-slate-600 bg-slate-100 rounded-md px-2.5 py-1 border border-slate-200 tracking-wider">
                                    {row.licensePlate || "UNREGISTERED"}
                                  </span>
                                  <button
                                    onClick={() =>
                                      copyToClipboard(
                                        row.licensePlate || "",
                                        `plate_${row.id}`,
                                      )
                                    }
                                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <svg
                                      width="16"
                                      height="16"
                                      viewBox="0 0 12 12"
                                      fill="none"
                                    >
                                      <path
                                        d="M2 2H7V7H2V2Z"
                                        stroke="#6B7280"
                                        strokeWidth="1"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M5 5H10V10H5V5Z"
                                        stroke="#6B7280"
                                        strokeWidth="1"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </button>
                                  {copiedField === `plate_${row.id}` && (
                                    <span className="text-[8px] text-green-600 font-normal">
                                      ✓
                                    </span>
                                  )}
                                </div>
                              </td>
                              {/* FIX: Use the correct fields from the vehicle object */}
                              <td className="p-4 font-medium text-gray-700">
                                {formatCurrency(row.fuel || 0)}
                              </td>
                              <td className="p-4 font-medium text-gray-700">
                                {formatCurrency(row.extraKm || 0)}
                              </td>
                              <td className="p-4 font-medium text-gray-700">
                                {formatCurrency(row.extraHrs || 0)}
                              </td>
                              <td className="p-4 font-medium text-gray-700">
                                {formatCurrency(
                                  row.deliveryAndPickupCharges || 0,
                                )}
                              </td>
                              <td className="p-4 font-medium text-gray-700">
                                {formatCurrency(row.downtimeCharges || 0)}
                              </td>
                              <td className="p-4 font-black text-indigo-600">
                                {formatCurrency(totalAddon)}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="self-stretch px-6 py-4 border-t-[0.80px] border-gray-200 inline-flex justify-between items-center">
                <div className="text-gray-500 text-sm font-normal">
                  Showing {tablePage * tableRowsPerPage + 1} to{" "}
                  {Math.min(
                    (tablePage + 1) * tableRowsPerPage,
                    filteredData.length,
                  )}{" "}
                  of {filteredData.length} vehicles
                </div>
                <div className="flex items-center gap-2">
                  <button
                    disabled={tablePage === 0}
                    onClick={() => setTablePage((p) => p - 1)}
                    className="p-2 rounded-[10px] outline outline-[0.80px] outline-gray-300 disabled:opacity-50 hover:bg-gray-50"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M10 4L6 8L10 12"
                        stroke="#1A1A1A"
                        strokeWidth="1.33333"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                  <div className="text-gray-700 text-sm font-medium">
                    Page {tablePage + 1} of{" "}
                    {Math.ceil(filteredData.length / tableRowsPerPage)}
                  </div>
                  <button
                    disabled={
                      (tablePage + 1) * tableRowsPerPage >= filteredData.length
                    }
                    onClick={() => setTablePage((p) => p + 1)}
                    className="p-2 rounded-[10px] outline outline-[0.80px] outline-gray-300 disabled:opacity-50 hover:bg-gray-50"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M6 4L10 8L6 12"
                        stroke="#1A1A1A"
                        strokeWidth="1.33333"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
